import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Split from 'react-split';
import './Items.css';
import {
  FaPlus, FaRegSave, FaTrash, FaEdit, FaSearch, FaTimes, FaUndo,
  FaDownload, FaUpload, FaInfoCircle, FaBarcode, FaHistory, FaFilePdf,
  FaFileCsv, FaShopify, FaQuestionCircle
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
const API = import.meta.env.VITE_API_URL;

const DEFAULT_LIMIT = 50;
const LS_COL_WIDTHS = 'items-col-widths.v1';
const MIN_COL_WIDTH = 70;
const MAX_COL_WIDTH = 720;
const CLIENT_FETCH_BATCH = 1000;
const ITEM_THUMB_STYLE = {
  width: 38,
  height: 38,
  objectFit: 'cover',
  borderRadius: 4,
  border: '1px solid #444',
  background: '#fff',
  transition: 'transform 0.15s cubic-bezier(.4,1.2,.6,1), box-shadow 0.15s',
  boxShadow: '0 1px 2px rgba(0,0,0,0.35)',
};

// ---- Volume helpers (in³ → ft³ = /1728) ----
const num = (v) => {
  const n = parseFloat(v); return Number.isFinite(n) ? n : 0;
};
const volFt3 = (L, W, H) => {
  const ci = num(L) * num(W) * num(H);
  return +(ci / 1728).toFixed(4);
};
const usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatUSD = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? usdFormatter.format(n) : '';
};
const clampMoney = (value) => {
  if (value == null || value === '') return 0;
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.round(n * 100) / 100);
};

const isHttpUrl = (s) => typeof s === 'string' && /^https?:\/\//i.test(s.trim());
const looksLikeBase64 = (s) => typeof s === 'string' && s.length > 64 && /^[A-Za-z0-9+/=]+$/.test(s.replace(/\s+/g, ''));
const renderPreviewImageCell = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (raw.startsWith('data:image/')) {
    return <img src={raw} alt="item" className="preview-thumb" />;
  }
  if (isHttpUrl(raw)) {
    return (
      <a href={raw} target="_blank" rel="noreferrer" title={raw}>
        <img src={raw} alt="item" className="preview-thumb" />
      </a>
    );
  }
  if (looksLikeBase64(raw)) {
    return <img src={`data:image/jpeg;base64,${raw}`} alt="item" className="preview-thumb" />;
  }
  return raw;
};


const parseCsvText = (input) => {
  const text = String(input || '');
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const filtered = lines.filter(line => line.trim().length);
  if (!filtered.length) return { headers: [], rows: [] };
  const split = (line) => {
    const out = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        out.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map(cell => cell.trim());
  };
  const headers = split(filtered[0]);
  const rows = filtered.slice(1).map(split);
  return { headers, rows };
};

const normalizeHeaderName = (value) => String(value || '').replace(/[^a-z0-9]/gi, '').toLowerCase();

const normalizeLabelTargets = (targets) => {
  if (!Array.isArray(targets)) return [];
  return targets
    .map((entry) => {
      if (entry && typeof entry === 'object') {
        const itemid = Number(entry.itemid ?? entry.id ?? entry.itemId);
        if (!Number.isFinite(itemid) || itemid <= 0) return null;
        const copiesRaw = Number(entry.copies ?? entry.count ?? entry.qty ?? entry.quantity ?? 1);
        const copies = Number.isFinite(copiesRaw) && copiesRaw > 0 ? Math.floor(copiesRaw) : 1;
        return {
          itemid,
          copies,
          sku: entry.sku || '',
          description: entry.description || '',
        };
      }
      const itemid = Number(entry);
      if (!Number.isFinite(itemid) || itemid <= 0) return null;
      return { itemid, copies: 1, sku: '', description: '' };
    })
    .filter(Boolean);
};
const countTotalLabels = (entries) => entries.reduce((sum, entry) => sum + Math.max(1, Number(entry?.copies || 1)), 0);

// ---------- role helpers ----------
function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}') || {};
  } catch { return {}; }
}
function isClientUser(u) {
  const v = u.isclient ?? u.isClient ?? u.role ?? u.roleid;
  if (v === true) return true;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y' || s === 'client';
  }
  return false;
}
function getUserClientId(u) {
  const raw = u.clientid ?? u.clientId ?? u.client_id ?? '';
  if (raw || raw === 0) return String(raw);
  if (Array.isArray(u?.allowedClientIds)) {
    const allowed = u.allowedClientIds
      .map((cid) => Number(cid))
      .filter((cid) => Number.isFinite(cid) && cid > 0);
    if (allowed.length === 1) return String(allowed[0]);
  }
  return '';
}
function getUserClientName(u) { return u.clientname ?? u.clientName ?? u.client ?? ''; }

function resolveClientOptionIdByName(options, rawName) {
  const target = String(rawName || '').trim().toLowerCase();
  if (!target) return '';
  const exact = (options || []).find((opt) => String(opt?.name || '').trim().toLowerCase() === target);
  if (exact?.id != null) return String(exact.id);
  const startsWith = (options || []).find((opt) => String(opt?.name || '').trim().toLowerCase().startsWith(target));
  if (startsWith?.id != null) return String(startsWith.id);
  const contains = (options || []).find((opt) => String(opt?.name || '').trim().toLowerCase().includes(target));
  return contains?.id != null ? String(contains.id) : '';
}

// -----------------------------------
const loadStoredWidths = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    const raw = window.localStorage.getItem(LS_COL_WIDTHS);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {}
  return {};
};

const clampColWidth = (value) => {
  const numVal = Number(value);
  if (!Number.isFinite(numVal)) return MIN_COL_WIDTH;
  return Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, Math.round(numVal)));
};

const emptyItem = {
  itemid: null,
  deleted: 0,
  sku: '',
  upc: '',
  hscode: '',
  default_country_of_origin: '',
  default_package_type: '',
  default_weight_uom: '',
  default_qty_uom: '',
  manufacturer_id_code: '',
  description: '',
  partnumber: '',
  clientid: '',
  supplier_id: null,
  supplier_name: '',
  supplier_code: '',
  length: 0,
  width: 0,
  height: 0,
  weight: 0,
  volume: 0,
  itemlength: 0,
  itemwidth: 0,
  itemheight: 0,
  itemweight: 0,
  itemvolume: 0,
  unitmeasurement: '',
  unitqty: 1,
  default_unit_value: '',
  style: '',
  color: '',
  size: '',
  image1: '',
  own_shipping_container: 0, // Added new field
  autoalloc: 1,
  preferred_package_id: null, // <-- NEW: preferred shipping package
  lowthreshold: '',
};

const CLIENT_EDITABLE_FIELDS = ['length', 'width', 'height', 'weight', 'default_unit_value'];
const CLIENT_MUTABLE_FIELDS = [...CLIENT_EDITABLE_FIELDS, 'volume', 'image1'];

// LocalStorage getters (sticky filters)
const getInitialItemsQ = () => localStorage.getItem('itemsQ') || '';
const getInitialSelectedClientId = () => localStorage.getItem('itemsSelectedClientId') || '__ALL__';
const getInitialLimit = () => {
  const stored = window.localStorage.getItem('itemsLimit');
  return stored ? Math.min(Math.max(parseInt(stored, 10) || DEFAULT_LIMIT, 1), 5000) : DEFAULT_LIMIT;
};

export default function Items() {
  const location = useLocation();
  const navigate = useNavigate();
  const ALL_VALUE = '__ALL__';
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, limit: DEFAULT_LIMIT, offset: 0 });

  // Logged-in user + role lock
  const user = getCurrentUser();
  const IS_CLIENT = isClientUser(user);
  const LOCKED_CLIENT_ID = getUserClientId(user);
  const LOCKED_CLIENT_NAME = getUserClientName(user);

  // Sticky filters
  const [q, setQ] = useState(getInitialItemsQ());
  const [selectedClientId, setSelectedClientId] = useState(getInitialSelectedClientId());

  // Pagination (match Orders)
  const [isTypingLimit, setIsTypingLimit] = useState(false);
  const [limit, setLimit] = useState(getInitialLimit());
  const [limitInput, setLimitInput] = useState(String(getInitialLimit()));
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortField, setSortField] = useState('sku');
  const [sortOrder, setSortOrder] = useState('ASC');

  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const clientTypeaheadRef = useRef(null);
  const [clientActiveIndex, setClientActiveIndex] = useState(-1);
  const clientOptionsRef = useRef([]);
  const clientSearchEditedRef = useRef(false);
  const clientSearchRaw = useMemo(() => clientSearchInput.trim(), [clientSearchInput]);
  const clientSearchTerm = useMemo(() => clientSearchRaw.toLowerCase(), [clientSearchRaw]);
  const prevSelectedClientId = useRef(null);
  const pendingAgentClientNameRef = useRef('');
  const itemQueryRef = useRef('');
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  // the client id we actually use in queries (client users can choose among allowed)
  useEffect(() => {
    if (IS_CLIENT && LOCKED_CLIENT_ID && (selectedClientId === ALL_VALUE || !selectedClientId)) {
      setSelectedClientId(String(LOCKED_CLIENT_ID));
    }
  }, [IS_CLIENT, LOCKED_CLIENT_ID]);

  useEffect(() => {
    let params;
    try {
      params = new URLSearchParams(location.search || '');
    } catch {
      return;
    }

    if ((params.get('agent_view') || '').trim().toLowerCase() !== 'items') return;

    const nextQ = String(params.get('agent_q') || '').trim();
    const nextAgentClientId = String(params.get('agent_clientid') || '').trim();
    const nextClientName = String(params.get('agent_clientname') || '').trim();
    const nextClientId = nextAgentClientId || resolveClientOptionIdByName(clientOptionsRef.current, nextClientName);

    pendingAgentClientNameRef.current = nextAgentClientId ? '' : nextClientName;
    setQ(nextQ);
    setShowDeleted(false);
    setSelected(null);
    setEditing(false);
    setCreating(false);
    setSelectedIds(new Set());
    setMeta((prev) => ({ ...prev, offset: 0 }));

    if (nextClientId) {
      setSelectedClientId(nextClientId);
      if (nextClientName) {
        clientSearchEditedRef.current = false;
        setClientSearchInput(nextClientName);
      }
      fetchItems(0, limit, nextClientId);
    } else if (!nextClientName) {
      setSelectedClientId(ALL_VALUE);
      setClientSearchInput('');
      fetchItems(0, limit, '');
    } else {
      setSelectedClientId('');
      setClientSearchInput(nextClientName);
    }

    ['agent_view', 'agent_q', 'agent_clientid', 'agent_clientname'].forEach((key) => params.delete(key));

    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [ALL_VALUE, location.pathname, location.search, navigate]);

  useEffect(() => {
    let cancelled = false;
    let params;
    try {
      params = new URLSearchParams(location.search || '');
    } catch {
      return () => { cancelled = true; };
    }

    const requestedItemId = Number(params.get('itemid') || 0) || 0;
    if (!requestedItemId) {
      itemQueryRef.current = '';
      return () => { cancelled = true; };
    }
    if (itemQueryRef.current === String(requestedItemId)) {
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        const item = await fetchJSON(`${API}/api/items/${requestedItemId}`);
        if (cancelled || !item?.itemid) return;
        itemQueryRef.current = String(requestedItemId);
        setShowDeleted(false);
        setSelectedIds(new Set());
        if (item?.clientid) {
          const nextClientId = String(item.clientid);
          setSelectedClientId(nextClientId);
          fetchItems(0, limit, nextClientId);
        }
        onEdit(item);
      } catch {
        if (!cancelled) {
          itemQueryRef.current = '';
        }
      }
    })();

    return () => { cancelled = true; };
  }, [location.search]);

  const currentClientId = IS_CLIENT
    ? (selectedClientId === ALL_VALUE ? '' : (selectedClientId || LOCKED_CLIENT_ID || ''))
    : (selectedClientId === ALL_VALUE ? '' : selectedClientId);
  const clientEditingLimited = IS_CLIENT && editing && !creating;
  const isClientFieldDisabled = (field) => clientEditingLimited && !CLIENT_EDITABLE_FIELDS.includes(field);
  const canChangeClient = !IS_CLIENT || (IS_CLIENT && clientOptions.length > 1);
  // --- NEW: Shipping package options for the dropdown (global + client) ---
  const [pkgOptions, setPkgOptions] = useState([]);            // flattened list
  const [pkgGroups, setPkgGroups] = useState({ global: [], client: [] }); // for optgroups

  // --- Import / Preview state & progress ---
  const importInputRef = useRef(null);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewErrors, setPreviewErrors] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importPickerOpen, setImportPickerOpen] = useState(false);
  const [shopifyStoreModalOpen, setShopifyStoreModalOpen] = useState(false);
  const [shopifyStoreIntegrations, setShopifyStoreIntegrations] = useState([]);
  const [shopifyStoreSelectedId, setShopifyStoreSelectedId] = useState('');
  const [shopifyStoreLoading, setShopifyStoreLoading] = useState(false);
  const [shopifyStoreError, setShopifyStoreError] = useState('');
  const [shopifyHelpOpen, setShopifyHelpOpen] = useState(false);
  const [templateHelpOpen, setTemplateHelpOpen] = useState(false);

  // Progress modal (shared by preview & import)
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const xhrRef = useRef(null);
  const originalItemRef = useRef(null);

  // Import job (server-side) + cancel/poll
  const [importLoading, setImportLoading] = useState(false);
  const [currentImportJobId, setCurrentImportJobId] = useState(null);
  const pollAbortRef = useRef(false);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const tableWrapRef = useRef(null);
  const dragColKeyRef = useRef(null);
  const resizingColRef = useRef(null);

  // Label print modal
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelItem, setLabelItem] = useState(null);
  const [labelPaper, setLabelPaper] = useState('4x6');
  const [labelTargets, setLabelTargets] = useState([]);
  const labelCsvInputRef = useRef(null);
  const labelCsvLookupCacheRef = useRef(new Map());
  const [labelCsvModalOpen, setLabelCsvModalOpen] = useState(false);
  const [labelCsvRows, setLabelCsvRows] = useState([]);
  const [labelCsvErrors, setLabelCsvErrors] = useState([]);
  const [labelCsvLoading, setLabelCsvLoading] = useState(false);
  const [labelCsvFileName, setLabelCsvFileName] = useState('');
  const labelTargetEntries = useMemo(() => normalizeLabelTargets(labelTargets), [labelTargets]);
  const labelTargetTotal = useMemo(() => countTotalLabels(labelTargetEntries), [labelTargetEntries]);
  const labelCsvValidRows = useMemo(() => labelCsvRows.filter(row => row.status === 'ok' && row.item?.itemid && row.qty > 0), [labelCsvRows]);
  const labelCsvTotalLabels = useMemo(
    () => labelCsvValidRows.reduce((sum, row) => sum + Math.max(1, Number(row.qty || 1)), 0),
    [labelCsvValidRows]
  );

  const openLabelModal = (item) => {
    setLabelItem(item);
    setLabelPaper('4x6');
    setLabelTargets(item?.itemid ? [{
      itemid: item.itemid,
      copies: 1,
      sku: item?.sku || '',
      description: item?.description || ''
    }] : []);
    setLabelOpen(true);
  };
  const printLabel = () => {
    const fallback = (labelItem?.itemid
      ? [{ itemid: labelItem.itemid, copies: 1, sku: labelItem?.sku || '', description: labelItem?.description || '' }]
      : []
    );
    const targets = normalizeLabelTargets(
      Array.isArray(labelTargets) && labelTargets.length ? labelTargets : fallback
    );
    if (!targets.length) return;

    const totalLabels = countTotalLabels(targets);

    if (labelPaper === 'ol125wx' && totalLabels > 1) {
      // Multi-item, multi-label sheet (10 labels per OL125WX sheet, like WarehouseConfig)
      const params = new URLSearchParams();
      params.set('ids', targets.map(t => t.itemid).join(','));
      params.set('paper', 'ol125wx');
      if (targets.some(t => (t.copies || 1) > 1)) {
        params.set('counts', targets.map(t => Math.max(1, Number(t.copies || 1))).join(','));
      }
      params.set('v', Date.now());
      const url = `${API}/api/items/labels?${params.toString()}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Single-item or non-OL125WX: one label PDF per item (per copy)
      const queue = [];
      targets.forEach((target) => {
        const totalCopies = Math.max(1, Number(target.copies || 1));
        for (let i = 0; i < totalCopies; i++) queue.push(target.itemid);
      });
      const idsQueue = queue.length ? queue : (labelItem?.itemid ? [labelItem.itemid] : []);
      idsQueue.forEach((id) => {
        const url = `${API}/api/items/${encodeURIComponent(id)}/label.pdf?paper=${encodeURIComponent(labelPaper)}&v=${Date.now()}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      });
    }
    setLabelOpen(false);
  };

  // Open UPC-A (Letter) barcode for current item in editor
  const openUpcPdf = () => {
    const id = selected?.itemid;
    if (!id) return;
    const url = `${API}/api/items/${id}/upc.pdf?upc=${encodeURIComponent(selected?.upc || '')}&v=${Date.now()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // --- Item History Modal state ---
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState(null); // { itemid, sku, description }
  const [historyInvRows, setHistoryInvRows] = useState([]); // receipt-level summaries
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedInvForHist, setSelectedInvForHist] = useState(null); // inventoryid
  const [movementRows, setMovementRows] = useState([]); // movement timeline for selected inventory
  const summaryRows = useMemo(() => {
    const arr = Array.isArray(historyInvRows) ? historyInvRows.slice() : [];
    arr.sort((a,b) => {
      const da = new Date(a.recvdate || 0).getTime() || 0;
      const db = new Date(b.recvdate || 0).getTime() || 0;
      return da - db;
    });
    return arr.map(r => ({
      date: r.recvdate,
      totalOut: Number(r.total_out_pieces || 0) || 0,
      receiptid: r.receiptid
    }));
  }, [historyInvRows]);

  // Resolve item image (URL/data URL/base64/Buffer-like)
  const resolveItemImage = (row) => {
    const isDataUrl = (s) => typeof s === 'string' && s.trim().startsWith('data:image/');
    const isHttpUrl = (s) => typeof s === 'string' && (/^https?:\/\//i.test(s) || s.startsWith('/'));
    const isB64 = (s) => typeof s === 'string' && s.replace(/\s+/g, '').length > 64 && /^[A-Za-z0-9+/=]+$/.test(s.replace(/\s+/g, ''));
    const urlFields = ['itemimageurl','item_image_url','imageurl','image_url','imageUrl','thumb','thumbnail'];
    for (const f of urlFields) {
      const v = row?.[f];
      if (v && (isHttpUrl(v) || isDataUrl(v))) return String(v).trim();
    }
    const baseFields = ['image1','itemimage1','image','img','image_base64','imageB64'];
    for (const f of baseFields) {
      const v = row?.[f]; if (!v) continue;
      if (typeof v === 'string') {
        const s = v.trim();
        if (isDataUrl(s)) return s;
        if (isB64(s)) return `data:image/jpeg;base64,${s.replace(/\s+/g, '')}`;
      }
      if (v && typeof v === 'object') {
        const d = Array.isArray(v?.data) ? v.data : (Array.isArray(v) ? v : null);
        if (d) {
          try { const u8 = new Uint8Array(d); const blob = new Blob([u8], { type: 'image/jpeg' }); return URL.createObjectURL(blob); }
          catch { /* ignore */ }
        }
      }
    }
    return '';
  };

  const openItemHistory = async (item) => {
    if (!item?.itemid) return;
    // Also select the clicked item so its row is marked active
    setSelected({ itemid: item.itemid });
    const imgSrc = resolveItemImage(item);
    setHistoryItem({ itemid: item.itemid, sku: item.sku, description: item.description, imageSrc: imgSrc });
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryInvRows([]);
    setSelectedInvForHist(null);
    setMovementRows([]);
    try {
      const params = new URLSearchParams();
      if (currentClientId) params.set('clientid', String(currentClientId));
      const url = `${API}/api/items/${item.itemid}/movements${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url);
      const j = await res.json().catch(() => ({ movements: [] }));
      const arr = Array.isArray(j?.movements) ? j.movements : [];
      // Convert to a simple receipts list for the left pane
      setHistoryInvRows(arr.map(m => ({
        inventoryid: m.inventoryid,
        receiptid: m.receiptid,
        recvdate: m.recvdate,
        palletnumber: m.palletnumber,
        bin: m.bin,
        bindisplay: m.bindisplay,
        locationtypeid: m.locationtypeid,
        location: m.location,
        start_pieces: m.start_pieces,
        on_hand_pieces: m.on_hand_pieces,
        total_out_pieces: m.total_out_pieces,
        moves: m.moves,
      })));
      if (arr.length) selectHistoryInv(arr[0]);
    } catch {
      setHistoryInvRows([]);
    } finally { setHistoryLoading(false); }
  };

  const selectHistoryInv = async (row) => {
    const invId = Number(row?.inventoryid || 0);
    setSelectedInvForHist(invId || null);
    if (!invId) { setMovementRows([]); return; }
    // movements are precomputed in left pane rows
    const hit = historyInvRows.find(r => Number(r.inventoryid) === invId);
    setMovementRows(hit?.moves || []);
  };
  const scrollSelectedIntoView = (behavior = 'smooth') => {
    if (!selected?.itemid) return;
    const wrap = tableWrapRef.current;
    if (!wrap) return;
    const doScroll = () => {
      const row = wrap.querySelector(`tr[data-itemid="${selected.itemid}"]`);
      if (row && typeof row.scrollIntoView === 'function') {
        try { row.scrollIntoView({ block: 'center', inline: 'nearest', behavior }); }
        catch { row.scrollIntoView(); }
      }
    };
    try { requestAnimationFrame(doScroll); } catch { setTimeout(doScroll, 0); }
  };

  // Page helpers use limit like Orders
  const page = useMemo(
    () => Math.floor((meta.offset || 0) / (limit || DEFAULT_LIMIT)) + 1,
    [meta.offset, limit]
  );
  const lastPage = useMemo(
    () => Math.max(1, Math.ceil((meta.total || 0) / (limit || DEFAULT_LIMIT))),
    [meta.total, limit]
  );

  // --- Split sizes (persisted) ---
  const getInitialSplitSizes = () => {
    try {
      const stored = localStorage.getItem('itemsSplitSizes');
      if (stored) {
        const s = JSON.parse(stored);
        if (Array.isArray(s) && s.length === 2) return s;
      }
      const rightPct = parseInt(localStorage.getItem('itemsItemsSplitPct') || '', 10);
      if (!Number.isNaN(rightPct)) return [100 - rightPct, rightPct];
    } catch { }
    return [60, 40];
  };
  const [splitSizes, setSplitSizes] = useState(getInitialSplitSizes);

  const fetchJSON = async (url, opts) => {
    const res = await fetch(url, { credentials: 'include', ...(opts || {}) });
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const b = await res.json();
        if (b?.error) msg = b.error;
      } catch { }
      throw new Error(msg);
    }
    return res.json();
  };

  const fetchItems = async (offset = 0, customLimit = limit, clientid = '') => {
    setLoadingList(true);
    try {
      const params = new URLSearchParams({
        limit: customLimit,
        offset,
        sort: sortField,
        order: sortOrder,
      });
      if (q) params.set('q', q);
      if (showDeleted) params.set('deleted', 1);
      if (clientid) params.set('clientid', clientid);
      const json = await fetchJSON(`${API}/api/items?${params.toString()}`);
      setItems(json.data || json || []);
      setMeta(json.meta || { total: (json.data || json).length, limit: customLimit, offset });
    } catch (e) {
      setItems([]);
      setMeta({ total: 0, limit: customLimit, offset });
    } finally {
      setLoadingList(false);
    }
  };

  const fetchClientOptions = async (signal) => {
    try {
      if (IS_CLIENT) {
        const res = await fetch(`${API}/api/reports/filters/clients`, { signal });
        const data = res.ok ? await res.json() : [];
        const opts = (Array.isArray(data) ? data : []).map(c => ({
          id: String(c.id ?? c.clientid ?? ''),
          name: c.name ?? c.client ?? `Client #${c.id}`,
        }));
        setClientOptions(opts);
        if (!selectedClientId && opts.length === 1) {
          setSelectedClientId(String(opts[0].id));
        }
      } else {
        let offset = 0;
        const seen = new Map();
        while (true) {
          const params = new URLSearchParams({
            limit: String(CLIENT_FETCH_BATCH),
            offset: String(offset),
            sortField: 'name',
            sortOrder: 'ASC',
          });
          const json = await fetchJSON(`${API}/api/clients?${params.toString()}`, { signal });
          const rows = Array.isArray(json?.data) ? json.data : [];
          rows.forEach((c) => {
            const id = String(c.id ?? c.clientid ?? '');
            if (!id) return;
            seen.set(id, {
              id,
              name: c.name ?? c.clientname ?? `Client #${c.id}`,
            });
          });
          if (rows.length < CLIENT_FETCH_BATCH) break;
          offset += CLIENT_FETCH_BATCH;
        }
        setClientOptions(Array.from(seen.values()));
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setClientOptions([]);
    }
  };

const fetchSupplierOptions = async () => {
  setLoadingSuppliers(true);
  try {
      const params = new URLSearchParams({
        limit: '500',
        offset: '0',
        sortField: 'name',
        sortOrder: 'ASC',
        active: 'active',
      });
      const json = await fetchJSON(`${API}/api/suppliers?${params.toString()}`);
      setSupplierOptions(Array.isArray(json?.data) ? json.data : []);
    } catch (err) {
      console.error('Failed to load suppliers', err);
      setSupplierOptions([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };
  
  useEffect(() => {
    clientOptionsRef.current = clientOptions;
  }, [clientOptions]);

  useEffect(() => {
    const pendingName = String(pendingAgentClientNameRef.current || '').trim();
    if (!pendingName) return;
    const resolvedId = resolveClientOptionIdByName(clientOptions, pendingName);
    if (!resolvedId) return;
    pendingAgentClientNameRef.current = '';
    setSelectedClientId(resolvedId);
    const match = clientOptions.find((opt) => String(opt.id) === String(resolvedId));
    if (match?.name) {
      clientSearchEditedRef.current = false;
      setClientSearchInput(match.name);
    }
    fetchItems(0, limit, resolvedId);
  }, [clientOptions]);

  useEffect(() => {
    const onClick = (e) => {
      if (clientTypeaheadRef.current && !clientTypeaheadRef.current.contains(e.target)) {
        setClientDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (prevSelectedClientId.current === selectedClientId) return;
    prevSelectedClientId.current = selectedClientId;
    if (selectedClientId === ALL_VALUE) {
      clientSearchEditedRef.current = false;
      setClientSearchInput('');
    } else if (selectedClientId) {
      const match = clientOptionsRef.current.find(opt => String(opt.id) === String(selectedClientId));
      if (match) {
        clientSearchEditedRef.current = false;
        setClientSearchInput(match.name);
      }
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedClientId || selectedClientId === ALL_VALUE) return;
    if (clientSearchEditedRef.current) return;
    if (clientSearchInput) return;
    const match = clientOptions.find(opt => String(opt.id) === String(selectedClientId));
    if (match) {
      clientSearchEditedRef.current = false;
      setClientSearchInput(match.name);
    }
  }, [clientOptions, clientSearchInput, selectedClientId]);

  useEffect(() => {
    if (IS_CLIENT) return;
    const controller = new AbortController();
    fetchClientOptions(controller.signal);
    return () => controller.abort();
  }, [IS_CLIENT]);

  // --- NEW: load shipping packages for editor dropdown ---
  const formatPkgLabel = (p) => {
    const dims = [p.length, p.width, p.height].filter(v => v != null && v !== '').join('×');
    const unit = (p.dim_unit || 'IN');
    const code = p.code ? ` (${p.code})` : '';
    const vol = (typeof p.volume_ft === 'number' || typeof p.volume_ft3 === 'number')
      ? (p.volume_ft ?? p.volume_ft3)
      : null;
    const volTxt = vol != null ? ` – ${Number(vol).toFixed(3)} ft³` : '';
    return `${p.name || 'Package'}${code} – ${dims} ${unit}${volTxt}`;
  };

  const fetchPackageOptions = async (clientid) => {
    try {
      // For client users, shipping packages endpoints are internal-only; skip fetching.
      if (IS_CLIENT) {
        setPkgGroups({ global: [], client: [] });
        setPkgOptions([]);
        return;
      }
      // Get global (no client filter) + client-scoped, active only, non-deleted
      const globalUrl = `${API}/api/shipping-packages?active=1`;
      const clientUrl = clientid ? `${API}/api/shipping-packages?clientid=${encodeURIComponent(clientid)}&active=1` : null;

      const [globalRes, clientRes] = await Promise.all([
        fetchJSON(globalUrl),
        clientUrl ? fetchJSON(clientUrl) : Promise.resolve({ data: [] })
      ]);

      const global = Array.isArray(globalRes?.data) ? globalRes.data : [];
      const client = Array.isArray(clientRes?.data) ? clientRes.data : [];

      // Deduplicate by id just in case
      const seen = new Set();
      const all = [...global, ...client].filter(p => {
        if (!p || seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });

      setPkgGroups({
        global: global,
        client: client
      });
      setPkgOptions(all);
    } catch (e) {
      setPkgGroups({ global: [], client: [] });
      setPkgOptions([]);
    }
  };

  // On mount: load clients; then fetch with current selection (including ALL)
useEffect(() => {
  if (IS_CLIENT) fetchClientOptions();
  fetchSupplierOptions();
  fetchItems(meta.offset, limit, currentClientId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // Re-fetch on sort / deleted / *selected client* / role lock changes
  useEffect(() => {
    fetchItems(meta.offset, limit, currentClientId);
    // also refresh package options when client context changes
    if (currentClientId) fetchPackageOptions(currentClientId);
    else { setPkgOptions([]); setPkgGroups({ global: [], client: [] }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder, showDeleted, selectedClientId, IS_CLIENT, LOCKED_CLIENT_ID]);

  // When the editor's client changes (during create), refresh packages
  useEffect(() => {
    if (creating && selected?.clientid) {
      fetchPackageOptions(selected.clientid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creating, selected?.clientid]);

  // Auto-search as SKU/description query changes
  useEffect(() => {
    fetchItems(0, limit, currentClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Persist filters to localStorage (internal users only)
  useEffect(() => {
    localStorage.setItem('itemsQ', q);
  }, [q]);

  useEffect(() => {
    // Persist the raw selection value (so __ALL__ survives reload)
    try { localStorage.setItem('itemsSelectedClientId', selectedClientId || ''); } catch {}
  }, [selectedClientId]);

  // Persist limit to localStorage
  useEffect(() => { localStorage.setItem('itemsLimit', String(limit)); }, [limit]);

  // Esc to close preview modal
  useEffect(() => {
    function onKeyDown(e) {
      if (!showImportPreview) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        cancelImportProgress();
        setShowImportPreview(false);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showImportPreview]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Items ', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [meta?.total]);

  const gotoPage = (p) => {
    const nextOffset = Math.max(0, (p - 1) * limit);
    fetchItems(nextOffset, limit, currentClientId);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    else { setSortField(field); setSortOrder('ASC'); }
  };

  const handleLimitUpdate = (raw) => {
    let v = parseInt(raw ?? limitInput, 10);
    if (isNaN(v)) v = limit;
    if (v < 1) v = 1;
    if (v > 5000) v = 5000;
    setLimit(v);
    setLimitInput(String(v));
    window.localStorage.setItem('itemsLimit', v);
    fetchItems(0, v, currentClientId);
  };

  const onEdit = (item) => {
    const pkgVol = volFt3(item?.length, item?.width, item?.height);
    const itmVol = volFt3(item?.itemlength, item?.itemwidth, item?.itemheight);
    const normalized = { ...emptyItem, ...item, volume: pkgVol, itemvolume: itmVol };
    setSelected(normalized);
    originalItemRef.current = { ...normalized };
    setEditing(true);
    setCreating(false);
  };

  // When opening the editor for an existing item, ensure selected row is visible
  useEffect(() => {
    if (!editing || !selected?.itemid) return;
    scrollSelectedIntoView('smooth');
  }, [editing, selected?.itemid]);

  useEffect(() => {
    if (!editing) originalItemRef.current = null;
  }, [editing]);

  // When closing the editor, keep selection and ensure it is visible again
  useEffect(() => {
    if (editing || creating) return;
    if (!selected?.itemid) return;
    scrollSelectedIntoView('smooth');
  }, [editing, creating, selected?.itemid]);

  // After items reload/refresh, re-scroll selection into view
  useEffect(() => {
    if (editing || creating) return;
    if (!selected?.itemid) return;
    scrollSelectedIntoView('auto');
  }, [items]);

  const onNew = () => {
    const base = { ...emptyItem, clientid: currentClientId || '' };
    base.volume = volFt3(base.length, base.width, base.height);
    base.itemvolume = volFt3(base.itemlength, base.itemwidth, base.itemheight);
    setSelected(base);
    setCreating(true);
    setEditing(false);
  };

  // ======= Column order (draggable headers) =======
  const LS_COL_ORDER = 'items-col-order.v1';
  const DEFAULT_DATA_COLS = [
    'client',
    'supplier',
    'sku',
    'qtyUnitsAvail',
    'qtyPiecesAvail',
    'lowthreshold',
    'image',
    'description',
    'partnumber',
    'upc',
    'hsCode',
    'selfContained',
    'autoAlloc',
    'length',
    'width',
    'height',
    'weight',
    'volume',
    'defaultUnitValue',
    'style',
    'color',
    'size',
  ];
  const loadColOrder = () => {
    try {
      const raw = localStorage.getItem(LS_COL_ORDER);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr)) {
        const filtered = arr.filter(k => DEFAULT_DATA_COLS.includes(k));
        const missing = DEFAULT_DATA_COLS.filter(k => !filtered.includes(k));
        if (filtered.length) return filtered.concat(missing);
      }
    } catch {}
    return DEFAULT_DATA_COLS.slice();
  };
  const [colOrder, setColOrder] = useState(loadColOrder);
  useEffect(() => {
    try { localStorage.setItem(LS_COL_ORDER, JSON.stringify(colOrder)); } catch {}
  }, [colOrder]);

  const [colWidths, setColWidths] = useState(loadStoredWidths);
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(LS_COL_WIDTHS, JSON.stringify(colWidths));
      }
    } catch {}
  }, [colWidths]);

  const onResizeMouseDown = useCallback((key, event) => {
    event.preventDefault();
    event.stopPropagation();
    const th = event.currentTarget.closest('th');
    const currentWidth = th?.getBoundingClientRect().width ?? colWidths[key] ?? 140;
    resizingColRef.current = {
      key,
      startX: event.clientX,
      startWidth: currentWidth
    };
  }, [colWidths]);

  useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    const handleMouseMove = (event) => {
      const info = resizingColRef.current;
      if (!info) return;
      const delta = event.clientX - info.startX;
      const nextWidth = clampColWidth((info.startWidth || 0) + delta);
      setColWidths((prev) => {
        if (prev[info.key] === nextWidth) return prev;
        return { ...prev, [info.key]: nextWidth };
      });
    };
    const handleMouseUp = () => {
      if (resizingColRef.current) {
        resizingColRef.current = null;
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [setColWidths]);

  // Column meta: label + renderers
  const renderItemThumbnail = (item) => {
    const src = resolveItemImage(item);
    if (!src) return null;
    const alt = item?.sku ? `${item.sku} thumbnail` : 'Item thumbnail';
    return (
      <img
        src={src}
        alt={alt}
        className="item-thumb-img"
        style={ITEM_THUMB_STYLE}
      />
    );
  };

  const supplierLookup = useMemo(() => {
    const map = new Map();
    (Array.isArray(supplierOptions) ? supplierOptions : []).forEach(opt => {
      if (opt && typeof opt.id !== 'undefined') {
        map.set(String(opt.id), opt);
      }
    });
    return map;
  }, [supplierOptions]);

  const filteredClientOptions = useMemo(() => {
    if (!clientOptions.length) return [];
    const term = clientSearchTerm;
    if (!term) return clientOptions.slice(0, 25);
    const normalizedTerm = term;
    const scored = clientOptions
      .map(opt => {
        const label = (opt.name || '').toLowerCase();
        if (!label.includes(normalizedTerm)) return null;
        let score = 1;
        if (label.startsWith(normalizedTerm)) score += 3;
        const words = label.split(/[\s\-_/]+/);
        if (words.some(w => w.startsWith(normalizedTerm))) score += 2;
        return { opt, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 25)
      .map(entry => entry.opt);
    return scored;
  }, [clientOptions, clientSearchTerm]);

  useEffect(() => {
    if (!clientDropdownOpen) {
      setClientActiveIndex(-1);
      return;
    }
    setClientActiveIndex(filteredClientOptions.length > 0 ? 0 : -1);
  }, [clientDropdownOpen, filteredClientOptions.length, clientSearchTerm]);

  const handleClientPick = (id, label) => {
    const normalized = id === ALL_VALUE ? ALL_VALUE : String(id);
    setSelectedClientId(normalized);
    setClientDropdownOpen(false);
    setClientSearchInput(normalized === ALL_VALUE ? '' : label);
    clientSearchEditedRef.current = false;
    const cid = normalized === ALL_VALUE ? '' : normalized;
    fetchItems(0, limit, cid);
  };
  
  const handleClientInputKeyDown = (e) => {
    if (!canChangeClient) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setClientDropdownOpen(true);
      setClientActiveIndex(prev => {
        if (filteredClientOptions.length === 0) return -1;
        const next = prev + 1;
        if (prev < -1) return 0;
        if (prev === -1) return filteredClientOptions.length > 0 ? 0 : -1;
        return Math.min(next, filteredClientOptions.length - 1);
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setClientDropdownOpen(true);
      setClientActiveIndex(prev => {
        if (filteredClientOptions.length === 0) return -1;
        if (prev <= 0) return -1;
        return prev - 1;
      });
    } else if (e.key === 'Enter') {
      if (!clientDropdownOpen && filteredClientOptions.length === 0) return;
      e.preventDefault();
      if (clientActiveIndex === -1) {
        if (!clientSearchTerm) {
          handleClientPick(ALL_VALUE, '');
        } else {
          setClientDropdownOpen(false);
        }
      } else if (filteredClientOptions[clientActiveIndex]) {
        const opt = filteredClientOptions[clientActiveIndex];
        handleClientPick(opt.id, opt.name);
      }
    } else if (e.key === 'Escape') {
      setClientDropdownOpen(false);
      setClientActiveIndex(-1);
    }
  };

  const columnMeta = useMemo(() => ({
    client: {
      label: 'Client',
      sortable: 'clientid',
      headerTitle: undefined,
      cell: (item) => (
        item.clientname
        || clientOptions.find(c => String(c.id) === String(item.clientid))?.name
        || item.clientid
      ),
    },
    supplier: {
      label: 'Linked Supplier',
      cell: (item) => {
        if (item.supplier_name && item.supplier_code) {
          return `${item.supplier_name} (${item.supplier_code})`;
        }
        return item.supplier_name || '—';
      }
    },
    sku: { label: 'SKU', sortable: 'sku', cell: (item) => item.sku },
    qtyUnitsAvail: {
      label: 'Qty Units Avail',
      cell: (item) => {
        const units = Number(item.qtyunitsonhand ?? item.qty_units_on_hand ?? 0);
        const perUnitCandidates = [
          item.inv_qtyperunit,
          item.qtyperunit,
          item.original_qtyperunit,
          item.qty_per_unit,
          item.qtyitemsper,
          item.qty_items_per,
          item.qtyitems_per_unit,
          item.qty_items_per_unit,
          item.itemsperunit,
          item.items_per_unit,
          item.itemsper,
          item.items_per,
          item.caseqty,
          item.case_qty,
          item.unitqty,
          item.unit_qty,
          item.unitsper,
          item.units_per,
        ];
        const perUnitRaw = perUnitCandidates.reduce((acc, candidate) => {
          if (acc != null) return acc;
          const parsed = Number(candidate);
          return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        }, null);
        const unitsText = Number.isFinite(units) ? units.toLocaleString() : '0';
        const perUnit = Number.isFinite(perUnitRaw) && perUnitRaw > 0 ? perUnitRaw : null;
        if (perUnit && Number.isFinite(units) && units > 0) {
          return (
            <span>
              {unitsText}{' '}
              <span style={{ fontSize: '0.85em', fontStyle: 'italic', color: 'rgba(0,0,0,0.75)' }}>
                ({perUnit.toLocaleString()} per unit)
              </span>
            </span>
          );
        }
        return unitsText;
      },
      headerTitle: 'Qty Units Available',
    },
    qtyPiecesAvail: { label: 'Qty Pieces Avail', cell: (item) => Number(item.piecesonhand || 0).toLocaleString(), headerTitle: 'Qty Pieces Available' },
    lowthreshold: {
      label: 'Low Threshold',
      sortable: 'lowthreshold',
      cell: (item) => {
        if (item.lowthreshold == null) return '—';
        const val = Number(item.lowthreshold);
        if (!Number.isFinite(val)) return '—';
        if (val < 0) return 'n/a';
        return val.toLocaleString();
      },
      headerTitle: 'Low inventory alert threshold (units)'
    },
    image: {
      label: 'Image',
      cell: (item) => renderItemThumbnail(item),
    },
    description: { label: 'Description', sortable: 'description', cell: (item) => item.description },
    partnumber: { label: 'Part #', sortable: 'partnumber', cell: (item) => item.partnumber },
    upc: { label: 'UPC', sortable: 'upc', cell: (item) => item.upc },
    hsCode: { label: 'HS Code', sortable: 'hscode', cell: (item) => item.hscode },
    selfContained: { label: 'Self Contained', cell: (item) => (Number(item.own_shipping_container) === 1 ? 'Yes' : 'No'), className: 'selfcol' },
    autoAlloc: { label: 'Auto Alloc', cell: (item) => (Number(item.autoalloc) === 1 ? 'Yes' : 'No') },
    length: { label: 'Length', sortable: 'length', cell: (item) => item.length },
    width: { label: 'Width', sortable: 'width', cell: (item) => item.width },
    height: { label: 'Height', sortable: 'height', cell: (item) => item.height },
    weight: { label: 'Weight', sortable: 'weight', cell: (item) => item.weight },
    volume: { label: 'Volume', sortable: 'volume', cell: (item) => item.volume },
    defaultUnitValue: {
      label: 'Default Unit Value',
      sortable: 'default_unit_value',
      cell: (item) => formatUSD(item.default_unit_value),
    },
    style: { label: 'Style', sortable: 'style', cell: (item) => item.style },
    color: { label: 'Color', sortable: 'color', cell: (item) => item.color },
    size: { label: 'Size', sortable: 'size', cell: (item) => item.size },
  }), [clientOptions]);

  const onColDragStart = (key, e) => {
    dragColKeyRef.current = key;
    try { e.dataTransfer.setData('text/plain', key); } catch {}
    e.dataTransfer.effectAllowed = 'move';
  };
  const onColDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onColDrop = (overKey, e) => {
    e.preventDefault();
    const fromKey = dragColKeyRef.current;
    if (!fromKey || fromKey === overKey) return;
    setColOrder(prev => {
      const arr = prev.slice();
      const fromIdx = arr.indexOf(fromKey);
      const toIdx = arr.indexOf(overKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, fromKey);
      return arr;
    });
  };

  const saveItem = async () => {
    const payload = { ...selected };
    payload.sku = String(payload.sku || '').trim();
    const effectiveClientId = Number(payload.clientid || currentClientId || 0);
    if (!payload.sku) {
      alert('SKU is required.');
      return;
    }
    if (!Number.isFinite(effectiveClientId) || effectiveClientId <= 0) {
      alert('Client is required.');
      return;
    }

    setSaving(true);
    try {
      payload.clientid = effectiveClientId;
      delete payload.clientname; // from join
      delete payload.preferred_package_name;
      delete payload.supplier_name;
      delete payload.supplier_code;
      // remove read-only availability fields not stored in items table
      delete payload.qtyunitsonhand;
      delete payload.piecesonhand;
      if (clientEditingLimited && !creating) {
        const original = originalItemRef.current || {};
        Object.keys(payload).forEach((key) => {
          if (!CLIENT_MUTABLE_FIELDS.includes(key)) {
            if (Object.prototype.hasOwnProperty.call(original, key)) {
              payload[key] = original[key];
            }
          }
        });
      }

      // allow '' to be treated as null for preferred_package_id
      if (payload.preferred_package_id === '' || payload.preferred_package_id == null) {
        payload.preferred_package_id = null;
      } else {
        const n = Number(payload.preferred_package_id);
        payload.preferred_package_id = Number.isFinite(n) ? n : null;
      }
      if (payload.supplier_id === '' || payload.supplier_id == null) {
        payload.supplier_id = null;
      } else {
        const supplierNum = Number(payload.supplier_id);
        payload.supplier_id = Number.isFinite(supplierNum) ? supplierNum : null;
      }
      if (payload.lowthreshold === '' || payload.lowthreshold == null) {
        payload.lowthreshold = null;
      } else {
        const lowNum = Number(payload.lowthreshold);
      payload.lowthreshold = Number.isFinite(lowNum) ? lowNum : null;
      }
      payload.hscode = String(payload.hscode || '').trim();
      payload.default_unit_value = clampMoney(payload.default_unit_value);

      const method = !creating ? 'PUT' : 'POST';
      const url = !creating
        ? `${API}/api/items/${selected.itemid}`
        : `${API}/api/items`;

      await fetchJSON(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      fetchItems(meta.offset, limit, currentClientId);
      // Keep last row selected when saving an edit; clear only after creating new
      if (creating) setSelected(null);
      setEditing(false);
      setCreating(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const restoreItem = async (itemid) => {
    if (!window.confirm('Restore this item?')) return;
    try {
      await fetchJSON(`${API}/api/items/${itemid}/restore`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      fetchItems(meta.offset, limit, currentClientId);
      if (selected?.itemid === itemid) setSelected(null);
    } catch (e) { alert(e.message); }
  };

  const deleteItem = async (itemid) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await fetchJSON(`${API}/api/items/${itemid}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      fetchItems(meta.offset, limit, currentClientId);
    } catch (e) { alert(e.message); }
  };

  // ---------- CSV Template, Preview & Import ----------
  const itemsSampleCsv = `sku,description,partnumber,upc,hscode,clientid,supplier_id,length,width,height,weight,volume,itemlength,itemwidth,itemheight,itemweight,itemvolume,unitmeasurement,unitqty,default_unit_value,style,color,size,image1,default_country_of_origin,default_package_type,default_weight_uom,default_qty_uom,manufacturer_id_code,preferred_package_id,lowthreshold,own_shipping_container,autoalloc
ABC-001,Widget A,PA-1001,012345678901,847130,123,45,10,8,6,2.5,0.28,10,8,6,2.5,0.28,EA,1,19.99,Classic,Red,L,https://cdn.example.com/items/abc-001.jpg,CN,Carton,LB,EA,MID1234567890,,25,0,1
XYZ-777,Gadget Z,GZ-777,098765432109,950300,123,,12,9,5,3.1,0.31,12,9,5,3.1,0.31,CASE,1,8.5,Sport,Blue,M,data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA,MX,Pallet,KG,CASE,MID0987654321,,10,1,1`;

  function downloadTextFile(filename, text) {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.style.display = 'none';
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
  }
  function handleDownloadTemplate() {
    downloadTextFile('items-import-template.csv', itemsSampleCsv);
  }

  // XHR-based upload for Preview → returns jobId
  function xhrPreviewUploadReturnJobId({ file }) {
    return new Promise((resolve, reject) => {
      const fd = new FormData();
      fd.append('file', file);
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;
      xhr.open('POST', `${API}/api/items/preview2`, true);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.max(1, Math.min(60, Math.round((e.loaded / e.total) * 60))); // 0..60
          setProgressPct(pct);
          setProgressMsg(`Uploading… ${pct}%`);
        } else {
          setProgressMsg('Uploading…');
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.onabort = () => reject(new Error('Upload canceled'));
      xhr.onload = () => {
        const text = xhr.responseText || '';
        let json = null;
        try { json = text ? JSON.parse(text) : {}; } catch {}
        if (json && xhr.status >= 200 && xhr.status < 300 && json.success && json.jobId) {
          resolve(json.jobId);
          return;
        }
        if (json?.error) {
          reject(new Error(json.error));
          return;
        }
        const snippet = text ? text.slice(0, 200) : '';
        reject(new Error(snippet ? `Invalid server response: ${snippet}` : 'Invalid server response'));
      };
      xhr.send(fd);
    });
  }

  // Poll preview status until done
  async function pollPreviewStatus(jobId, { intervalMs = 300 } = {}) {
    setProgressMsg('Parsing on server…');
    setProgressPct((p) => Math.max(p, 62));
    let done = false;
    while (!done) {
      const resp = await fetch(`${API}/api/items/preview2/status/${jobId}`);
      if (!resp.ok) throw new Error(`Status error (${resp.status})`);
      const json = await resp.json();
      if (!json.success) throw new Error(json.error || 'Status failed');
      if (!json.done) {
        const pct = 62 + Math.floor((json.progress / 100) * 36); // 62..98
        setProgressPct(Math.max(62, Math.min(pct, 98)));
        setProgressMsg(`Parsing on server… ${json.progress}%`);
        await new Promise(r => setTimeout(r, intervalMs));
      } else {
        done = true;
        if (json.error) throw new Error(json.error);
        setProgressPct(100);
        setProgressMsg('Done');
        return json.result || { data: [], errors: [] };
      }
    }
  }

  function handleOpenImport() {
    setImportPickerOpen(true);
  }

  const openShopifyStoreModal = async () => {
    if (!currentClientId) {
      alert('Select a client before importing from a Shopify store.');
      return;
    }
    setImportPickerOpen(false);
    setShopifyStoreModalOpen(true);
    setShopifyStoreIntegrations([]);
    setShopifyStoreSelectedId('');
    setShopifyStoreError('');
    setShopifyStoreLoading(true);
    try {
      const resp = await fetch(`${API}/api/integrations?clientid=${encodeURIComponent(currentClientId)}`);
      if (!resp.ok) {
        let msg = `Unable to load integrations (${resp.status})`;
        try {
          const json = await resp.json();
          if (json?.error) msg = json.error;
        } catch { /* ignore */ }
        throw new Error(msg);
      }
      const json = await resp.json();
      const rows = Array.isArray(json) ? json : [];
      const shops = rows.filter(row =>
        String(row?.servicetype || '').toLowerCase().includes('shopify')
      );
      setShopifyStoreIntegrations(shops);
      if (shops.length) setShopifyStoreSelectedId(String(shops[0].id));
      else setShopifyStoreError('No Shopify integrations found for this client.');
    } catch (err) {
      setShopifyStoreError(err.message || 'Unable to load Shopify integrations.');
    } finally {
      setShopifyStoreLoading(false);
    }
  };

  const handleShopifyStoreConfirm = async () => {
    if (!shopifyStoreSelectedId) {
      setShopifyStoreError('Select a Shopify integration.');
      return;
    }
    if (!currentClientId) {
      setShopifyStoreError('Select a client before importing.');
      return;
    }
    setShopifyStoreLoading(true);
    setShopifyStoreError('');
    try {
      const resp = await fetch(`${API}/api/shopify/integration/${encodeURIComponent(shopifyStoreSelectedId)}/items-export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientid: Number(currentClientId) }),
      });
      let json = {};
      try { json = await resp.json(); } catch { json = {}; }
      if (!resp.ok) throw new Error(json?.error || `Shopify request failed (${resp.status})`);
      if (!json?.csv) throw new Error('The Shopify store returned no products.');
      const blob = new Blob([json.csv], { type: 'text/csv' });
      const fileName = json.fileName || `shopify-store-${Date.now()}.csv`;
      const file = new File([blob], fileName, { type: 'text/csv' });
      setShopifyStoreModalOpen(false);
      await startPreviewForFile(file, { skipTypeCheck: true });
    } catch (err) {
      setShopifyStoreError(err.message || 'Unable to download products from Shopify.');
    } finally {
      setShopifyStoreLoading(false);
    }
  };

  const openTemplateHelp = (event) => {
    if (event) {
      event.stopPropagation();
      if (event.type === 'keydown') {
        const key = event.key.toLowerCase();
        if (key !== 'enter' && key !== ' ') return;
        event.preventDefault();
      }
    }
    setTemplateHelpOpen(true);
  };
  const closeTemplateHelp = () => setTemplateHelpOpen(false);

  const openShopifyHelp = (event) => {
    if (event) {
      event.stopPropagation();
      if (event.type === 'keydown') {
        const key = event.key.toLowerCase();
        if (key !== 'enter' && key !== ' ') return;
        event.preventDefault();
      }
    }
    setShopifyHelpOpen(true);
  };
  const closeShopifyHelp = () => setShopifyHelpOpen(false);

  const handleImportChoice = (mode) => {
    if (mode === 'template') {
      setImportPickerOpen(false);
      requestAnimationFrame(() => importInputRef.current?.click());
      return;
    }
    if (mode === 'shopify-store') {
      openShopifyStoreModal();
      return;
    }
  };

  const startPreviewForFile = async (file, { skipTypeCheck = false } = {}) => {
    if (!file) return;
    const name = file.name?.toLowerCase() || '';
    if (
      !skipTypeCheck &&
      !name.endsWith('.csv') &&
      file.type !== 'text/csv'
    ) {
      alert('Please select a valid CSV file.');
      return;
    }
    try {
      setSelectedFile(file);
      setImportLoading(true);
      setProgressOpen(true);
      setProgressPct(1);
      setProgressMsg('Uploading…');
      const jobId = await xhrPreviewUploadReturnJobId({ file });
      const result = await pollPreviewStatus(jobId, {});
      setPreviewData(result.data || []);
      setPreviewErrors(result.errors || []);
      setShowImportPreview(true);
    } catch (err) {
      if (err.message !== 'Upload canceled') alert(`Preview error: ${err.message}`);
      setPreviewData([]);
      setPreviewErrors([]);
      setSelectedFile(null);
    } finally {
      setImportLoading(false);
      setProgressOpen(false);
      setProgressMsg('');
      setProgressPct(0);
    }
  };

  const handlePreviewImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    startPreviewForFile(file);
    if (importInputRef.current) importInputRef.current.value = '';
  };

  // Upload file to /import2 and poll status (with cancel support)
  async function handleConfirmImport() {
    if (!selectedFile) {
      alert('No file available for import. Please upload a CSV file again.');
      setShowImportPreview(false);
      return;
    }
    setImportLoading(true);
    setProgressOpen(true);
    setProgressPct(1);
    setProgressMsg('Uploading…');
    try {
      const jobId = await new Promise((resolve, reject) => {
        const fd = new FormData();
        fd.append('file', selectedFile);
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.open('POST', `${API}/api/items/import2`, true);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.max(1, Math.min(60, Math.round((e.loaded / e.total) * 60)));
            setProgressPct(pct);
            setProgressMsg(`Uploading… ${pct}%`);
          } else setProgressMsg('Uploading…');
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.onabort = () => reject(new Error('Upload canceled'));
        xhr.onload = () => {
          try {
            const json = JSON.parse(xhr.responseText || '{}');
            if (xhr.status >= 200 && xhr.status < 300 && json.success && json.jobId) resolve(json.jobId);
            else reject(new Error(json.error || `Import start failed (${xhr.status})`));
          } catch { reject(new Error('Invalid server response')); }
        };
        xhr.send(fd);
      });
      setCurrentImportJobId(jobId);
      pollAbortRef.current = false;
      setProgressMsg('Importing on server…');
      setProgressPct((p) => Math.max(p, 62));
      let done = false;
      while (!done && !pollAbortRef.current) {
        const resp = await fetch(`${API}/api/items/import2/status/${jobId}`);
        if (!resp.ok) throw new Error(`Status error (${resp.status})`);
        const json = await resp.json();
        if (!json.success) throw new Error(json.error || 'Status failed');
        if (json.canceled || json.stage === 'canceled') {
          setProgressMsg('Canceled');
          setProgressPct((p) => Math.max(62, p));
          break;
        }
        if (!json.done) {
          const pct = 62 + Math.floor((json.progress / 100) * 36);
          setProgressPct(Math.max(62, Math.min(pct, 98)));
          setProgressMsg(`Importing on server… ${json.progress}%`);
          await new Promise(r => setTimeout(r, 300));
        } else {
          done = true;
          if (json.error) throw new Error(json.error);
          setProgressPct(100);
          setProgressMsg(`Imported ${json.result?.imported ?? 0} row(s)${(json.result?.errors?.length) ? `, ${json.result.errors.length} error(s)` : ''}`);
          fetchItems(meta.offset, limit, currentClientId);
          setShowImportPreview(false);
        }
      }
      setCurrentImportJobId(null);
      pollAbortRef.current = false;
    } catch (err) {
      if (err.message !== 'Upload canceled') alert(`Import error: ${err.message}`);
    } finally {
      setCurrentImportJobId(null);
      pollAbortRef.current = false;
      setImportLoading(false);
      setProgressOpen(false);
      setProgressMsg('');
      setProgressPct(0);
      setSelectedFile(null);
    }
  }

  // Cancel handlers
  const cancelPreviewUpload = () => {
    try { xhrRef.current?.abort(); } catch { }
    setProgressOpen(false);
    setProgressMsg('');
    setProgressPct(0);
    setImportLoading(false);
    if (importInputRef.current) importInputRef.current.value = '';
  };
  async function cancelImportProgress() {
    if (xhrRef.current && xhrRef.current.readyState !== 4) { try { xhrRef.current.abort(); } catch { } }
    pollAbortRef.current = true;
    if (currentImportJobId) {
      try { await fetch(`${API}/api/items/import2/cancel/${currentImportJobId}`, { method: 'POST' }); setProgressMsg('Canceling…'); }
      catch { /* ignore */ }
    }
  }
  const inImport = Boolean(currentImportJobId);

  // Export via backend route (respects filters)
  function handleExportItems() {
    const params = new URLSearchParams({
      sort: sortField,
      order: sortOrder,
    });
    if (q) params.set('q', q);
    if (showDeleted) params.set('deleted', 1);
    if (currentClientId) params.set('clientid', currentClientId);
    window.open(`${API}/api/items/export/csv?${params.toString()}`, '_self');
  }

  // Counts for preview summary & gating import
  const validCount = useMemo(() => previewData.filter(r => r.valid).length, [previewData]);
  const invalidCount = useMemo(() => Math.max(0, previewData.length - validCount), [previewData, validCount]);

  // ====== Bulk selection helpers ======
  function toggleRowSelection(id, checked) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  }
  const allSelected = useMemo(() => {
    if (!items.length) return false;
    return items.every(r => selectedIds.has(r.itemid));
  }, [items, selectedIds]);
  function toggleAllVisible() {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const everySelected = items.every(r => next.has(r.itemid));
      if (everySelected) items.forEach(r => next.delete(r.itemid));
      else items.forEach(r => next.add(r.itemid));
      return next;
    });
  }
  function clearSelection() { setSelectedIds(new Set()); }
  function openBulkDeleteConfirm() {
    if (selectedIds.size === 0 || deleting) return;
    setShowDeleteConfirm(true);
  }
  async function confermBulkDelete() {
    if (selectedIds.size === 0) { setShowDeleteConfirm(false); return; }
    setDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const resp = await fetch(`${API}/api/items/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      const json = await resp.json();
      if (!resp.ok || !json.success) throw new Error(json.error || 'Bulk delete failed');
      clearSelection();
      fetchItems(meta.offset, limit, currentClientId);
    } catch (err) {
      alert(`Delete error: ${err.message}`);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  const editorVisible = !!((selected && editing) || creating);

  const printLabelsForSelection = () => {
    const ids = Array.from(selectedIds || []).filter(Boolean);
    if (!ids.length) {
      alert('Select one or more items to print labels.');
      return;
    }
    const itemMap = new Map(items.map(it => [it.itemid, it]));
    const targets = ids.map((id) => {
      const hit = itemMap.get(id) || null;
      return {
        itemid: id,
        copies: 1,
        sku: hit?.sku || '',
        description: hit?.description || ''
      };
    });
    const first = targets.length ? itemMap.get(targets[0].itemid) || null : null;
    setLabelItem(first || null);
    // For larger batches, default to OL125WX (multi-label sheet); else 4x6
    setLabelPaper(targets.length >= 10 ? 'ol125wx' : '4x6');
    setLabelTargets(targets);
    setLabelOpen(true);
  };

  const resetLabelCsvState = () => {
    setLabelCsvRows([]);
    setLabelCsvErrors([]);
    setLabelCsvLoading(false);
    setLabelCsvFileName('');
  };
  const closeLabelCsvModal = () => {
    setLabelCsvModalOpen(false);
    resetLabelCsvState();
  };
  const openLabelCsvPicker = () => {
    if (labelCsvInputRef.current) {
      labelCsvInputRef.current.value = '';
    }
    setLabelCsvErrors([]);
    setLabelCsvRows([]);
    setLabelCsvFileName('');
    setLabelCsvModalOpen(false);
    labelCsvInputRef.current?.click();
  };
  const fetchItemForLabelCsv = async (clientid, sku) => {
    const params = new URLSearchParams();
    if (clientid) params.set('clientid', clientid);
    const paramString = params.toString();
    const url = `${API}/api/items/by-sku/${encodeURIComponent(sku)}${paramString ? `?${paramString}` : ''}`;
    const resp = await fetch(url, { credentials: 'include' });
    if (!resp.ok) {
      let msg = `${resp.status} ${resp.statusText}`;
      try {
        const body = await resp.json();
        if (body?.error) msg = body.error;
      } catch { }
      throw new Error(msg);
    }
    return resp.json();
  };
  const handleLabelCsvFileChange = async (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    setLabelCsvErrors([]);
    setLabelCsvRows([]);
    setLabelCsvFileName(file.name || 'labels.csv');
    setLabelCsvLoading(true);
    try {
      const text = await file.text();
      const { headers, rows } = parseCsvText(text);
      if (!headers.length || !rows.length) {
        setLabelCsvErrors(['CSV file is empty.']);
        return;
      }
      const normalizedHeaders = headers.map(normalizeHeaderName);
      const clientAliases = ['clientid', 'client', 'clientcode', 'clientname'];
      const skuAliases = ['sku', 'itemsku', 'productsku'];
      const qtyAliases = ['numberoflabels', 'labels', 'labelqty', 'labelcount', 'qty', 'quantity', 'qtylabels', 'labelquantity', 'numberlabels'];
      const clientIdx = normalizedHeaders.findIndex(h => clientAliases.includes(h));
      const skuIdx = normalizedHeaders.findIndex(h => skuAliases.includes(h));
      const qtyIdx = normalizedHeaders.findIndex(h => qtyAliases.includes(h));
      const missing = [];
      if (clientIdx === -1) missing.push('CLIENTID');
      if (skuIdx === -1) missing.push('SKU');
      if (qtyIdx === -1) missing.push('NUMBER OF LABELS');
      if (missing.length) {
        setLabelCsvErrors([`Missing required column(s): ${missing.join(', ')}`]);
        return;
      }
      const parsedRows = rows
        .map((cols, idx) => {
          const rowNumber = idx + 2; // header is row 1
          const clientid = String(cols[clientIdx] || '').trim();
          const sku = String(cols[skuIdx] || '').trim();
          const qtyRaw = cols[qtyIdx];
          const qtyNum = Number(String(qtyRaw ?? '').trim() || '1');
          const qty = Number.isFinite(qtyNum) && qtyNum > 0 ? Math.floor(qtyNum) : 0;
          if (!clientid && !sku) return null;
          let status = 'pending';
          let message = '';
          if (!clientid) { status = 'error'; message = 'Missing client id'; }
          else if (!sku) { status = 'error'; message = 'Missing SKU'; }
          else if (!qty) { status = 'error'; message = 'Invalid label quantity'; }
          return {
            rowNumber,
            clientid,
            sku,
            qty,
            status,
            message,
            item: null,
            cacheKey: `${clientid}||${sku}`.toLowerCase()
          };
        })
        .filter(Boolean);
      if (!parsedRows.length) {
        setLabelCsvErrors(['No usable rows found in CSV.']);
        return;
      }
      setLabelCsvRows(parsedRows);
      const resolvedRows = [];
      for (const row of parsedRows) {
        if (row.status !== 'pending') {
          resolvedRows.push(row);
          continue;
        }
        const cached = labelCsvLookupCacheRef.current.get(row.cacheKey);
        if (cached) {
          if (cached.ok) {
            resolvedRows.push({ ...row, status: 'ok', item: cached.item });
          } else {
            resolvedRows.push({ ...row, status: 'error', message: cached.message });
          }
          continue;
        }
        try {
          const itemData = await fetchItemForLabelCsv(row.clientid, row.sku);
          const payload = {
            itemid: itemData.itemid,
            sku: itemData.sku,
            description: itemData.description
          };
          labelCsvLookupCacheRef.current.set(row.cacheKey, { ok: true, item: payload });
          resolvedRows.push({ ...row, status: 'ok', item: payload });
        } catch (err) {
          const message = err?.message || 'Lookup failed';
          labelCsvLookupCacheRef.current.set(row.cacheKey, { ok: false, message });
          resolvedRows.push({ ...row, status: 'error', message });
        }
      }
      setLabelCsvRows(resolvedRows);
    } catch (err) {
      setLabelCsvErrors([err?.message || 'Unable to parse CSV.']);
    } finally {
      setLabelCsvLoading(false);
      setLabelCsvModalOpen(true);
      if (labelCsvInputRef.current) labelCsvInputRef.current.value = '';
    }
  };
  const startLabelPrintFromCsv = () => {
    const usableRows = labelCsvRows.filter(row => row.status === 'ok' && row.item?.itemid && row.qty > 0);
    if (!usableRows.length) {
      setLabelCsvErrors(['No valid rows available to print labels.']);
      return;
    }
    const aggregated = new Map();
    usableRows.forEach((row) => {
      const key = row.item.itemid;
      const copies = Math.max(1, Number(row.qty || 1));
      if (aggregated.has(key)) {
        aggregated.get(key).copies += copies;
      } else {
        aggregated.set(key, {
          itemid: key,
          copies,
          sku: row.item.sku || row.sku,
          description: row.item.description || ''
        });
      }
    });
    const targets = Array.from(aggregated.values());
    const totalLabels = countTotalLabels(targets);
    setLabelTargets(targets);
    setLabelItem(targets.length === 1 ? {
      itemid: targets[0].itemid,
      sku: targets[0].sku,
      description: targets[0].description
    } : null);
    setLabelPaper(totalLabels >= 10 ? 'ol125wx' : '4x6');
    setLabelOpen(true);
    closeLabelCsvModal();
  };

  // Left and Right Panes
  const leftPane = (
    <div className="items-left-pane">
      <div className="items-toolbar">
        <div className="items-toolbar-row filters-row">
          {/* Client: type-ahead picker when multiple are available */}
          {canChangeClient && (
            <div className="client-select client-typeahead" ref={clientTypeaheadRef}>
              <div
                className="typeahead-control search"
                onClick={() => setClientDropdownOpen(true)}
              >
                <FaSearch style={{ opacity: 0.7 }} />
                <input
                  id="client-typeahead-input"
                  name="items-client-typeahead"
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  aria-label="Client"
                  value={clientSearchInput}
                  onChange={(e) => {
                    clientSearchEditedRef.current = true;
                    setClientSearchInput(e.target.value);
                    setClientDropdownOpen(true);
                  }}
                  onFocus={(e) => {
                    setClientDropdownOpen(true);
                    e.target.select();
                  }}
                  onKeyDown={handleClientInputKeyDown}
                  placeholder={selectedClientId === ALL_VALUE ? 'All clients' : 'Search client…'}
                />
                {selectedClientId !== ALL_VALUE && (
                  <button
                    type="button"
                    className="clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClientPick(ALL_VALUE, '');
                    }}
                    aria-label="Clear client filter"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
              {clientDropdownOpen && (
                <div className="typeahead-list">
                  <button
                    type="button"
                    className={`typeahead-item ${selectedClientId === ALL_VALUE ? 'selected' : ''} ${clientActiveIndex === -1 ? 'focused' : ''}`}
                    onClick={() => handleClientPick(ALL_VALUE, '')}
                  >
                    All clients
                  </button>
                  {filteredClientOptions.length === 0 ? (
                    <div className="typeahead-empty">No matches</div>
                  ) : (
                    filteredClientOptions.map((opt, idx) => (
                      <button
                        type="button"
                        key={opt.id}
                        className={`typeahead-item ${String(selectedClientId) === String(opt.id) ? 'selected' : ''} ${clientActiveIndex === idx ? 'focused' : ''}`}
                        onClick={() => handleClientPick(opt.id, opt.name)}
                      >
                        {opt.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
          <div className="search">
            <FaSearch />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search SKU, Description, Part #"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  fetchItems(0, limit, currentClientId);
                }
              }}
            />
            {q && (
              <button className="clear" onClick={() => { setQ(''); fetchItems(0, limit, currentClientId); }}>
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        <div className="items-toolbar-row actions-row">
          <div className="items-toolbar-actions">
            {/* Actions in order: Search • New • Delete • Template • Import • Export */}
            <button className="btn" onClick={() => fetchItems(0, limit, currentClientId)}>
              <FaSearch style={{ marginRight: 6, verticalAlign: '-2px' }} />
              Search
            </button>
            {/* Internal-only actions */}
            {!IS_CLIENT && (
              <>
                <button className="btn primary" onClick={onNew} disabled={false}>
                  <FaPlus /> New
                </button>
                <button
                  className="btn danger"
                  onClick={openBulkDeleteConfirm}
                  disabled={selectedIds.size === 0}
                  title={selectedIds.size ? `Delete ${selectedIds.size} selected` : 'Delete (select rows)'}
                >
                  <FaTrash style={{ verticalAlign: '-2px', marginRight: 6 }} />
                  Delete{selectedIds.size ? ` (${selectedIds.size})` : ''}
                </button>
                <button
                  className="btn"
                  onClick={handleDownloadTemplate}
                  title="Download Items CSV Template"
                >
                  <FaDownload style={{ verticalAlign: '-2px' }} /> Template
                </button>
                <button
                  className="btn"
                  onClick={handleOpenImport}
                  title="Import Items from CSV"
                  disabled={importLoading || progressOpen}
                >
                  {(importLoading || progressOpen) && <span className="spinner" />}
                  <FaUpload style={{ verticalAlign: '-2px', marginRight: 6 }} />
                  {progressOpen ? 'Working…' : 'Import'}
                </button>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                  onChange={handlePreviewImport}
                />
              </>
            )}
            <button
              className="btn"
              onClick={handleExportItems}
              title="Export Items to CSV"
              disabled={!currentClientId}
            >
              <FaDownload style={{ verticalAlign: '-2px', marginRight: 6 }} />
              Export
            </button>
            {!IS_CLIENT && (
              <>
                <button
                  className="btn"
                  onClick={printLabelsForSelection}
                  disabled={selectedIds.size === 0}
                  title={selectedIds.size ? `Print labels for ${selectedIds.size} selected item(s)` : 'Select one or more rows first'}
                >
                  <FaFilePdf style={{ marginRight: 6, verticalAlign: '-2px' }} />
                  Print Label(s)
                </button>
                <button
                  className="btn"
                  onClick={openLabelCsvPicker}
                  title="Print labels by uploading a CSV"
                >
                  <FaFileCsv style={{ marginRight: 6, verticalAlign: '-2px' }} />
                  Print Labels CSV
                </button>
                <input
                  ref={labelCsvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                  onChange={handleLabelCsvFileChange}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Only this scrolls */}
      <div className="items-table-wrap" ref={tableWrapRef}>
        {loadingList ? (
          <div className="loading">Loading…</div>
        ) : (
          <table className="items-table">
            <thead>
              <tr>
                {!IS_CLIENT && (
                  <th className="select-col">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAllVisible}
                      aria-label="Select all visible"
                    />
                  </th>
                )}
                <th>Actions</th>
                {colOrder.map((key) => {
                  const meta = columnMeta[key] || {};
                  const sortable = meta.sortable;
                  const active = sortable && sortField === sortable;
                  const title = meta.headerTitle;
                  const width = colWidths?.[key];
                  const style = width ? { width: `${width}px`, minWidth: `${width}px` } : undefined;
                  return (
                    <th
                      key={key}
                      className={`${active ? 'active-sort' : ''} ${meta.className || ''}`.trim()}
                      title={title}
                      style={style}
                      onClick={sortable ? () => handleSort(sortable) : undefined}
                      onDragOver={onColDragOver}
                      onDrop={(e) => onColDrop(key, e)}
                    >
                      <span
                        className="col-drag-handle"
                        draggable
                        onDragStart={(e) => onColDragStart(key, e)}
                        onClick={(e) => e.stopPropagation()}
                        title="Drag to reorder"
                        aria-label={`Drag to move ${meta.label} column`}
                      >
                        ⋮⋮
                      </span>
                      <span className="col-label">
                        {meta.label}
                        {active ? (sortOrder === 'ASC' ? ' ▲' : ' ▼') : ''}
                      </span>
                      <span
                        className="col-resize-handle"
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(e) => onResizeMouseDown(key, e)}
                        onClick={(e) => e.stopPropagation()}
                        title="Drag horizontally to resize column"
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {items.length ? (
                items.map((item) => (
                  <tr
                    key={item.itemid}
                    data-itemid={item.itemid}
                    className={selected?.itemid === item.itemid ? 'active' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => { if (!editing && !creating) setSelected({ itemid: item.itemid }); }}
                  >
                    {!IS_CLIENT && (
                      <td className="select-col">
                        <input
                          type="checkbox"
                          className="row-checkbox"
                          checked={selectedIds.has(item.itemid)}
                          onChange={e => toggleRowSelection(item.itemid, e.target.checked)}
                          aria-label={`Select item ${item.itemid}`}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                    )}
                    <td className="actions" style={{ whiteSpace: 'nowrap' }}>
                    {!item.deleted ? (
                      <>
                        {/* View History visible to all users, including clients */}
                        <button className="mini-btn" title="View History" onClick={e => { e.stopPropagation(); openItemHistory(item); }}>
                          <FaHistory size={14} />
                        </button>
                        <button className="mini-btn" title="Edit" onClick={e => { e.stopPropagation(); setSelected({ itemid: item.itemid }); onEdit(item); }}>
                          <FaEdit size={14} />
                        </button>
                        {/* Internal-only actions */}
                        {!IS_CLIENT && (
                          <>
                            <button className="mini-btn" title="Print Label" onClick={e => { e.stopPropagation(); openLabelModal(item); }}>
                              <FaBarcode size={14} />
                            </button>

                            </>
                          )}
                        </>
                      ) : (
                        !IS_CLIENT ? (
                          <button className="mini-btn" title="Restore" style={{ color: 'green' }} onClick={e => { e.stopPropagation(); restoreItem(item.itemid); }}>
                            <FaUndo size={14} />
                          </button>
                        ) : null
                      )}
                    </td>
                    {colOrder.map((key) => {
                      const meta = columnMeta[key] || {};
                      const className = meta.className || '';
                      const width = colWidths?.[key];
                      const style = width ? { width: `${width}px`, minWidth: `${width}px` } : undefined;
                      return (
                        <td key={key} className={className} style={style}>
                          {meta.cell ? meta.cell(item) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={(IS_CLIENT ? 1 : 2) + colOrder.length} style={{ color: '#aaa' }}>
                    {selectedClientId && selectedClientId !== '' ? 'No items' : 'Choose a client to view items'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Item History Modal */}
      {historyOpen && (
        <div className="modal-backdrop items-modal-backdrop" onClick={() => setHistoryOpen(false)}>
          <div className="modal items-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ display:'flex', alignItems:'center', gap:12 }}>
              {historyItem?.imageSrc ? (
                <img
                  src={historyItem.imageSrc}
                  alt={historyItem?.sku || 'item'}
                  style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #444' }}
                />
              ) : null}
              <h3 style={{ flex:1 }}>Item History — {historyItem?.sku || ''}{historyItem?.description ? ` — ${historyItem.description}` : ''}</h3>
              {!!(historyItem?.itemid) && (
                <>
                  <button
                    className="btn"
                    title="Export CSV"
                    onClick={() => {
                      const url = `${API}/api/items/${historyItem.itemid}/movements/export.csv?clientid=${encodeURIComponent(currentClientId || '')}`;
                      window.open(url, '_blank', 'noopener');
                    }}
                  >
                    <FaDownload style={{ marginRight: 6 }} /> CSV
                  </button>
                  <button
                    className="btn"
                    title="Export PDF"
                    onClick={() => {
                      const url = `${API}/api/items/${historyItem.itemid}/movements/export.pdf?clientid=${encodeURIComponent(currentClientId || '')}`;
                      window.open(url, '_blank', 'noopener');
                    }}
                  >
                    <FaFilePdf style={{ marginRight: 6 }} /> PDF
                  </button>
                </>
              )}
              <button className="icon-btn" onClick={() => setHistoryOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body" style={{ minHeight: 300, maxHeight: '70vh', overflow:'auto' }}>
              {historyLoading ? (
                <div>Loading…</div>
              ) : (
                <>
                  <h4>Summary by Receipt</h4>
                  {(() => {
                    // Build combined "in" and per-movement "out" rows per receipt (grouped by receipt)
                    const rows = [];
                    const sorted = (Array.isArray(historyInvRows) ? [...historyInvRows] : []).sort((a,b) => {
                      const da = new Date(a.recvdate || 0).getTime() || 0;
                      const db = new Date(b.recvdate || 0).getTime() || 0;
                      return da - db;
                    });
                    for (const r of sorted) {
                      const date = r.recvdate;
                      const wr = r.receiptid;
                      const inQty = Number(r.start_pieces || 0) || 0;
                      const isPicking = ['6','3'].includes(String(r.locationtypeid || ''));
                      const binVal = isPicking ? (r.bindisplay || r.bin || '') : '';

                      // Row for when received (Total In) and NOT an adjustment
                      if (inQty > 0 ) {

                        rows.push({ date, qty: inQty, receiptid: wr, type: 'in', kind: '', orderNumber: '', bin: binVal });
                      }

                      // Rows for each outbound movement (orders + eorders) with their own dates
                      const moves = Array.isArray(r.moves) ? r.moves : [];
                      moves.forEach(m => {
                        const md = m.date || date; // fallback to receipt date if missing
                        // Derive bin from movement detail when available (handles picking move-out)
                        const detail = String(m.orderNumber || '');
                        const match = detail.match(/A\d+\-B\d+\-S\d+\-Slot\d+/i);
                        const moveBin = match ? match[0] : binVal;
                        if (m && m.kind === 'adjustment') {
                          const qIn  = Number(m.in_pieces || 0) || 0;
                          const qOut = Number(m.out_pieces || 0) || 0;
                          if (qIn > 0) rows.push({ date: md, qty: qIn, receiptid: wr, type: 'in',  kind: 'adjustment', orderNumber: m.orderNumber || '', bin: moveBin });
                          if (qOut > 0) rows.push({ date: md, qty: qOut, receiptid: wr, type: 'out', kind: 'adjustment', orderNumber: m.orderNumber || '', bin: moveBin });
                        } else {
                          const q = Number(m.out_pieces || 0) || 0;
                          if (q > 0) rows.push({ date: md, qty: q, receiptid: wr, type: 'out', kind: m.kind, orderNumber: m.orderNumber || m.refId || '', bin: moveBin });
                        }
                      });
                    }
          
                    // Global ascending sort by date/time; if exact same timestamp, show deductions (out) first
                    rows.sort((a, b) => {
                      const da = new Date(a.date || 0).getTime() || 0;
                      const db = new Date(b.date || 0).getTime() || 0;
                      if (da !== db) return da - db;
                      if (a.type !== b.type) return a.type === 'out' ? -1 : 1;
                      return 0;
                    });
                    // Filter for display and totals:
                    //  - keep all explicit movement kinds (order/eorder/adjustment)
                    //  - for synthetic Received (kind === '' and type === 'in'),
                    //    keep only the first per WR (receiptid)
                    const seenWRForReceived = new Set();
                    const filtered = rows.filter(r => {
                      const kind = String(r?.kind || '');
                      if (kind !== '') return true;
                      if (String(r?.type || '') !== 'in') return false;
                      const key = String(r?.receiptid || '');
                      if (!key) return false;
                      if (seenWRForReceived.has(key)) return false;
                      seenWRForReceived.add(key);
                      return true;
                    });

                    const totalSigned = filtered.reduce((acc, r) => acc + (r.type === 'out' ? -Number(r.qty || 0) : Number(r.qty || 0)), 0);
                    return filtered.length ? (
                      <table className="history-table">
                        <thead>
                          <tr><th>Date</th><th>In/Out (pcs)</th><th>WR #</th><th>Location</th><th>Type</th><th>Details</th></tr>
                        </thead>
                        <tbody>

                          {filtered.map((r, idx) => (
                            <tr key={`sum-${r.receiptid}-${idx}`}>
                              <td>{r.date ? new Date(r.date).toLocaleString() : ''}</td>
                              <td>{r.type === 'out' ? '-' : '+'}{r.qty}</td>
                              <td>{r.receiptid ? `WR${r.receiptid}` : ''}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{(() => {
                                const detail = String(r.orderNumber || '');
                                // Prefer parsed picking bin label, then combined from detail, then any pallet #
                                const binFromR = String(r.bin || '').trim();
                                const mBin = detail.match(/A\d+\-B\d+\-S\d+\-Slot\d+/i);
                                if (binFromR) return binFromR;
                                if (mBin) return mBin[0];
                                const mPal = detail.match(/Pallet\s*#\s*\d+/i) || detail.match(/#\s*\d+/);
                                if (mPal) {
                                  // Clean any stray closing parenthesis/brackets at the end
                                  return mPal[0].replace(/\s*[\)\]]+$/, '').trim();
                                }
                                return '';
                              })()}</td>
                              <td>{
                                r.kind === 'order' ? 'Order' :
                                r.kind === 'eorder' ? 'E‑Order' :
                                r.kind === 'adjustment' ? 'Adjustment' :
                                (r.type === 'in' ? 'Received' : '')
                              }</td>
                              {/* Show full detail for adjustments (location/pallet) and order numbers for orders */}
                              <td>{r.orderNumber || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td></td>
                            <td>{totalSigned}</td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    ) : (
                      <div>No summary available.</div>
                    );
                  })()}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setHistoryOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination stays outside the scroll area (match Orders) */}
      <div className="pagination">
        <button className="btn" disabled={page <= 1} onClick={() => gotoPage(1)}>&laquo;</button>
        <button className="btn" disabled={page <= 1} onClick={() => gotoPage(page - 1)}>Prev</button>
        <span>Page {page} of {lastPage}</span>
        <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(page + 1)}>Next</button>
        <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(lastPage)}>&raquo;</button>
        <label style={{ marginLeft: 12, fontSize: 13 }}>
          Rows per page:&nbsp;
          <input
            type="number"
            min={1}
            max={5000}
            value={limitInput}
            style={{ width: 60, fontSize: 13, padding: "2px 4px" }}
            onKeyDown={e => {
              setIsTypingLimit(true);
              if (e.key === 'Enter') {
                setIsTypingLimit(false);
                handleLimitUpdate(e.currentTarget.value);
              }
            }}
            onChange={e => {
              setLimitInput(e.target.value);
              if (!isTypingLimit) handleLimitUpdate(e.target.value);
            }}
            onBlur={e => {
              setIsTypingLimit(false);
              handleLimitUpdate(e.target.value);
            }}
          />
        </label>
      </div>
    </div>
  );

  const editorForm = (
    <>
      <div className="form-grid">
          <div className="item-editor-required-note">Fields marked <span aria-hidden="true">*</span> are required.</div>
          <label className="required-field">
            <span className="required-label">Client <span aria-hidden="true">*</span></span>
            <select
              className={`required-outline${!selected?.clientid ? ' required-input' : ''}`}
              value={selected?.clientid || ''}
              onChange={e => {
                const val = e.target.value;
                setSelected(s => ({ ...s, clientid: val }));
                // When creating and client changes, reload package options for that client
                if (creating) fetchPackageOptions(val);
              }}
              disabled={!creating} // allow client users to choose when creating
              required
            >
              <>
                <option value="">— Select —</option>
                {clientOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </>
            </select>
          </label>
          <label>
            Linked Supplier
            <select
              value={selected?.supplier_id != null ? String(selected.supplier_id) : ''}
              onChange={e => {
                const val = e.target.value;
                setSelected(s => {
                  const option = supplierLookup.get(val);
                  return {
                    ...s,
                    supplier_id: val === '' ? null : Number(val),
                    supplier_name: option?.name || '',
                    supplier_code: option?.supplier_code || ''
                  };
                });
              }}
              disabled={clientEditingLimited}
            >
              <option value="">— None —</option>
              {supplierOptions.map(opt => (
                <option key={opt.id} value={String(opt.id)}>
                  {opt.name}{opt.supplier_code ? ` (${opt.supplier_code})` : ''}
                </option>
              ))}
            </select>
            {loadingSuppliers && (
              <small className="field-hint" style={{ display: 'block', marginTop: 4 }}>
                Loading suppliers…
              </small>
            )}
          </label>

          <label className="required-field">
            <span className="required-label">SKU <span aria-hidden="true">*</span></span>
            <input
              className={`required-outline${!selected?.sku?.trim() ? ' required-input' : ''}`}
              value={selected?.sku || ''}
              onChange={e => setSelected(s => ({ ...s, sku: e.target.value }))}
              disabled={IS_CLIENT && !creating}
              required
            />
          </label>

          <label>
            Description
            <input
              value={selected?.description || ''}
              onChange={e => setSelected(s => ({ ...s, description: e.target.value }))}
              disabled={isClientFieldDisabled('description')}
            />
          </label>

          <label>
            Part #
            <input
              value={selected?.partnumber || ''}
              onChange={e => setSelected(s => ({ ...s, partnumber: e.target.value }))}
              disabled={isClientFieldDisabled('partnumber')}
            />
          </label>

          <label>
            UPC
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                className="mini-btn"
                title="Print UPC-A (Letter)"
                onClick={openUpcPdf}
                disabled={!selected?.itemid}
                aria-label="Print UPC-A barcode"
              >
                <FaBarcode />
              </button>
              <input
                value={selected?.upc || ''}
                onChange={e => setSelected(s => ({ ...s, upc: e.target.value }))}
                disabled={isClientFieldDisabled('upc')}
              />
            </div>
          </label>

          <label>
            HS Code
            <input
              value={selected?.hscode || ''}
              onChange={e => setSelected(s => ({ ...s, hscode: e.target.value }))}
              disabled={isClientFieldDisabled('hscode')}
              placeholder="e.g. 847130"
            />
          </label>

          <label>
            Country of Origin
            <input
              value={selected?.default_country_of_origin || ''}
              onChange={e => setSelected(s => ({ ...s, default_country_of_origin: e.target.value }))}
              disabled={clientEditingLimited}
              placeholder="e.g. China"
            />
          </label>

          <label>
            Default Qty UOM
            <input
              value={selected?.default_qty_uom || ''}
              onChange={e => setSelected(s => ({ ...s, default_qty_uom: e.target.value }))}
              disabled={clientEditingLimited}
              placeholder="e.g. PCS"
            />
          </label>

          <label>
            Default Package Type
            <input
              value={selected?.default_package_type || ''}
              onChange={e => setSelected(s => ({ ...s, default_package_type: e.target.value }))}
              disabled={clientEditingLimited}
              placeholder="e.g. Carton"
            />
          </label>

          <label>
            Default Weight UOM
            <input
              value={selected?.default_weight_uom || ''}
              onChange={e => setSelected(s => ({ ...s, default_weight_uom: e.target.value }))}
              disabled={clientEditingLimited}
              placeholder="e.g. KG"
            />
          </label>

          <label>
            Manufacturer ID Code
            <input
              value={selected?.manufacturer_id_code || ''}
              onChange={e => setSelected(s => ({ ...s, manufacturer_id_code: e.target.value }))}
              disabled={clientEditingLimited}
              placeholder="Optional MID"
            />
          </label>

          <label>
            Package Length (inches)
            <input
              type="number"
              min="0"
              value={selected?.length || ''}
              onChange={e =>
                setSelected(s => {
                  const v = e.target.value;
                  return { ...s, length: v, volume: volFt3(v, s.width, s.height) };
                })
              }
              disabled={isClientFieldDisabled('length')}
            />
          </label>
          <label>
            Package Width (inches)
            <input
              type="number"
              min="0"
              value={selected?.width || ''}
              onChange={e =>
                setSelected(s => {
                  const v = e.target.value;
                  return { ...s, width: v, volume: volFt3(s.length, v, s.height) };
                })
              }
              disabled={isClientFieldDisabled('width')}
            />
          </label>
          <label>
            Package Height (inches)
            <input
              type="number"
              min="0"
              value={selected?.height || ''}
              onChange={e =>
                setSelected(s => {
                  const v = e.target.value;
                  return { ...s, height: v, volume: volFt3(s.length, s.width, v) };
                })
              }
              disabled={isClientFieldDisabled('height')}
            />
          </label>
          <label>
            Package Weight (lbs)
            <input
              type="number"
              min="0"
              value={selected?.weight || ''}
              onChange={e => setSelected(s => ({ ...s, weight: e.target.value }))}
              disabled={isClientFieldDisabled('weight')}
            />
          </label>
          <label>
            Package Volume (ft3)
            <input
              type="number"
              min="0"
              value={selected?.volume || ''}
              onChange={e => setSelected(s => ({ ...s, volume: e.target.value }))}
              disabled={clientEditingLimited}
            />
          </label>
          <label>
            Default Unit Value (USD)
            <input
              type="number"
              min="0"
              step="0.01"
              value={selected?.default_unit_value ?? ''}
              onChange={e => setSelected(s => ({ ...s, default_unit_value: e.target.value }))}
              disabled={isClientFieldDisabled('default_unit_value')}
            />
          </label>
          <label>
            Low Threshold
            <input
              type="number"
              step="1"
              min="-1"
              value={selected?.lowthreshold ?? ''}
              onChange={e => setSelected(s => ({ ...s, lowthreshold: e.target.value }))}
              disabled={clientEditingLimited}
              placeholder="Leave blank to use default"
            />
            <small className="field-hint" style={{ display: 'block', marginTop: 4 }}>
              Enter -1 to disable low inventory alerts for this SKU.
            </small>
          </label>

          <label>
            Item Length (actual, inches)
            <input
              type="number"
              min="0"
              value={selected?.itemlength || ''}
              onChange={e =>
                setSelected(s => {
                  const v = e.target.value;
                  return { ...s, itemlength: v, itemvolume: volFt3(v, s.itemwidth, s.itemheight) };
                })
              }
              disabled={clientEditingLimited}
            />
          </label>
          <label>
            Item Width (actual, inches)
            <input
              type="number"
              min="0"
              value={selected?.itemwidth || ''}
              onChange={e =>
                setSelected(s => {
                  const v = e.target.value;
                  return { ...s, itemwidth: v, itemvolume: volFt3(s.itemlength, v, s.itemheight) };
                })
              }
              disabled={clientEditingLimited}
            />
          </label>
          <label>
            Item Height (actual, inches)
            <input
              type="number"
              min="0"
              value={selected?.itemheight || ''}
              onChange={e =>
                setSelected(s => {
                  const v = e.target.value;
                  return { ...s, itemheight: v, itemvolume: volFt3(s.itemlength, s.itemwidth, v) };
                })
              }
              disabled={clientEditingLimited}
            />
          </label>
          <label>
            Item Weight (actual, lbs)
            <input
              type="number"
              min="0"
              value={selected?.itemweight || ''}
              onChange={e => setSelected(s => ({ ...s, itemweight: e.target.value }))}
              disabled={clientEditingLimited}
            />
          </label>

          <label>
            Style
            <input
              value={selected?.style || ''}
              onChange={e => setSelected(s => ({ ...s, style: e.target.value }))}
              disabled={clientEditingLimited}
            />
          </label>
          <label>
            Color
            <input
              value={selected?.color || ''}
              onChange={e => setSelected(s => ({ ...s, color: e.target.value }))}
              placeholder="e.g. Red, Blue, White"
              disabled={clientEditingLimited}
            />
          </label>
          <label>
            Size
            <input
              value={selected?.size || ''}
              onChange={e => setSelected(s => ({ ...s, size: e.target.value }))}
              placeholder="e.g. Large, Medium, Small"
              disabled={clientEditingLimited}
            />
          </label>

          <label className="checkbox-inline">
            <input
              type="checkbox"
              checked={selected?.own_shipping_container === 1}
              onChange={e => setSelected(s => ({ ...s, own_shipping_container: e.target.checked ? 1 : 0 }))}
              disabled={clientEditingLimited}
            />
            <span>Own Shipping Container</span>
          </label>

          <label className="checkbox-inline">
            <input
              type="checkbox"
              checked={Number(selected?.autoalloc ?? 1) === 1}
              onChange={e => setSelected(s => ({ ...s, autoalloc: e.target.checked ? 1 : 0 }))}
              disabled={clientEditingLimited}
            />
            <span>Auto Allocate (E-Orders)</span>
          </label>

          {/* ================= NEW: Preferred Shipping Package ================= */}
          <label>
            Preferred Shipping Package
            <select
              value={selected?.preferred_package_id ?? ''}
              onChange={e => {
                const val = e.target.value;
                setSelected(s => ({
                  ...s,
                  preferred_package_id: val === '' ? null : Number(val)
                }));
              }}
              disabled={!selected?.clientid || clientEditingLimited} // require client & unlocked
            >
              <option value="">— None —</option>

              {/* Global packages */}
              {pkgGroups.global.length > 0 && (
                <optgroup label="All">
                  {pkgGroups.global.map(p => (
                    <option key={`g-${p.id}`} value={p.id}>
                      {formatPkgLabel(p)}
                    </option>
                  ))}
                </optgroup>
              )}

              {/* Client-scoped packages */}
              {pkgGroups.client.length > 0 && (
                <optgroup label="Client">
                  {pkgGroups.client.map(p => (
                    <option key={`c-${p.id}`} value={p.id}>
                      {formatPkgLabel(p)}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
          {/* =================================================================== */}

          <label>
            Image
            <input type="file" accept="image/*" onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onloadend = () => setSelected(s => ({ ...s, image1: reader.result }));
              reader.readAsDataURL(file);
            }} />
            {selected?.image1 && (
              <div style={{ marginTop: 8, position: 'relative', display: 'inline-block' }}>
                <img className='thumbNail'
                  src={selected.image1}
                  alt="Item"
                  style={{ borderRadius: 6, border: '1px solid #222', background: '#fff', display: 'block' }}
                />
                <button
                  type="button"
                  className="clear-img-btn"
                  title="Clear image"
                  style={{
                    position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.5)',
                    color: '#fff', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer'
                  }}
                  onClick={() => setSelected(s => ({ ...s, image1: '' }))}
                >
                  &times;
                </button>
              </div>
            )}
          </label>
        </div>
    </>
  );

  if (!editorVisible) {
    return (
      <div className="items-full-wrapper">
        {leftPane}
        {/* Import source picker */}
        {importPickerOpen && (
          <div className="modal-backdrop items-modal-backdrop" onClick={() => setImportPickerOpen(false)}>
            <div className="modal items-modal import-picker-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Import Items</h3>
                <button className="icon-btn" onClick={() => setImportPickerOpen(false)} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <div className="import-choice-grid">
                  <button
                    type="button"
                    className="import-choice-option"
                    onClick={() => handleImportChoice('template')}
                  >
                    <h4>
                      <span className="import-heading-with-help">
                        <span className="import-heading-label">
                          <FaFileCsv /> Easy3PL Template CSV
                        </span>
                        <span
                          role="button"
                          tabIndex={0}
                          className="import-help-btn"
                          aria-label="Open Easy3PL template import help"
                          onClick={openTemplateHelp}
                          onKeyDown={openTemplateHelp}
                        >
                          <FaQuestionCircle size={14} aria-hidden="true" />
                          <span className="import-help-btn-text">How does this work?</span>
                        </span>
                      </span>
                    </h4>
                    <p>Use the standard Easy3PL CSV template. You&apos;ll be prompted for a .csv file.</p>
                  </button>
                  <button
                    type="button"
                    className="import-choice-option"
                    onClick={() => handleImportChoice('shopify-store')}
                  >
                    <h4>
                      <span className="import-heading-with-help">
                        <span className="import-heading-label">
                          <FaShopify /> Shopify Store
                        </span>
                        <span
                          role="button"
                          tabIndex={0}
                          className="import-help-btn"
                          aria-label="Open Shopify Store import help"
                          onClick={openShopifyHelp}
                          onKeyDown={openShopifyHelp}
                        >
                          <FaQuestionCircle size={14} aria-hidden="true" />
                          <span className="import-help-btn-text">How does this work?</span>
                        </span>
                      </span>
                    </h4>
                    <p>Download SKUs directly from a configured Shopify integration.</p>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {templateHelpOpen && (
          <div
            className="modal-backdrop items-modal-backdrop"
            onClick={closeTemplateHelp}
          >
            <div
              className="modal items-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="templateHelpTitle"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="templateHelpTitle">Easy3PL Template CSV Import Help</h3>
                <button className="icon-btn" onClick={closeTemplateHelp} aria-label="Close help">×</button>
              </div>
              <div className="modal-body">
                <p>
                  Use the Easy3PL Template CSV option when you want full control over the items being uploaded.
                  Start by downloading the template from the Items page and fill each column with the required SKU
                  details. When you re-upload the filled file, the importer validates every row and shows you a preview
                  before anything is saved.
                </p>
                <div className="help-section">
                  <h4>Workflow</h4>
                  <ol>
                    <li>Download the Easy3PL CSV template (via the Items toolbar) and open it in Excel, Sheets, or similar.</li>
                    <li>Populate each row with your SKU data, keeping the provided column headers untouched.</li>
                    <li>Save the spreadsheet as a CSV file.</li>
                    <li>Return here, choose <strong>Easy3PL Template CSV</strong>, and upload the saved file.</li>
                    <li>Review the preview, resolve any validation errors, then confirm to import the items.</li>
                  </ol>
                </div>
                <div className="help-section">
                  <h4>Tips</h4>
                  <ul>
                    <li>Leave optional fields blank rather than deleting the columns.</li>
                    <li>Use unique SKUs; duplicates in the file will be flagged before import.</li>
                    <li>Units should match those configured for the selected client (e.g., lbs vs kg).</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn secondary" onClick={closeTemplateHelp}>Close</button>
              </div>
            </div>
          </div>
        )}

        {shopifyHelpOpen && (
          <div
            className="modal-backdrop items-modal-backdrop"
            onClick={closeShopifyHelp}
          >
            <div
              className="modal items-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="shopifyHelpTitle"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="shopifyHelpTitle">Shopify Store Import Help</h3>
                <button className="icon-btn" onClick={closeShopifyHelp} aria-label="Close help">×</button>
              </div>
              <div className="modal-body">
                <p>
                  The Shopify Store option lets you download every variant with a SKU from a client’s configured
                  Shopify integration and run it through the same preview/import flow used for CSV files.
                  Existing items are updated (without wiping populated dimensions/weights), while brand new SKUs are added automatically.
                </p>

                <div className="help-section">
                  <h4>Prerequisites</h4>
                  <ul>
                    <li>Select the client you want to manage from the Items list first.</li>
                    <li>Ensure that client already has at least one Shopify integration configured under Integrations.</li>
                    <li>You need internal access (or client access to that integration) to fetch products.</li>
                  </ul>
                </div>

                <div className="help-section">
                  <h4>How It Works</h4>
                  <ol>
                    <li>Open <strong>Import</strong> → choose <strong>Shopify Store</strong>.</li>
                    <li>Select the Shopify store (integration) you want to pull from and click <strong>Fetch Products</strong>.</li>
                    <li>Easy3PL calls the store’s Admin API, gathers every product/variant with a SKU, weight, dimensions, and image (when available), and builds an Easy3PL-ready CSV.</li>
                    <li>The preview window opens automatically so you can review and confirm before importing.</li>
                  </ol>
                </div>

                <div className="help-section">
                  <h4>Data Mapping Highlights</h4>
                  <ul>
                    <li><code>clientid</code> is set to the currently selected client.</li>
                    <li><code>sku</code>, <code>description</code>, <code>partnumber</code>, and <code>upc</code> come from variant data.</li>
                    <li>Weights use Shopify’s weight + unit fields (falling back to grams when needed).</li>
                    <li>Dimensions are pulled from explicit dimension metafields when present; otherwise we scan image alt text and SEO description for “L x W x H”.</li>
                    <li>Image URLs use the variant image, falling back to the product default image.</li>
                  </ul>
                </div>

                <div className="help-section">
                  <h4>Tips &amp; Troubleshooting</h4>
                  <ul>
                    <li>If a Shopify store has many products, fetching may take a minute—watch the progress indicator.</li>
                    <li>Rows without SKUs are skipped automatically.</li>
                    <li>Dimension/weight blanks will not overwrite existing values during import.</li>
                    <li>Use the preview filters/search to verify critical SKUs before clicking <strong>Import</strong>.</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={closeShopifyHelp}>Close</button>
              </div>
            </div>
          </div>
        )}

        {shopifyStoreModalOpen && (
          <div
            className="modal-backdrop items-modal-backdrop"
            onClick={() => { if (!shopifyStoreLoading) setShopifyStoreModalOpen(false); }}
          >
            <div
              className="modal items-modal import-picker-modal"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Import From Shopify Store</h3>
                <button
                  className="icon-btn"
                  onClick={() => { if (!shopifyStoreLoading) setShopifyStoreModalOpen(false); }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {shopifyStoreLoading && <p>Loading Shopify integrations…</p>}
                {!shopifyStoreLoading && (
                  <>
                    {shopifyStoreIntegrations.length > 0 ? (
                      <>
                        <label className="shopify-store-select-label">
                          Shopify Integration
                          <select
                            value={shopifyStoreSelectedId}
                            onChange={(e) => setShopifyStoreSelectedId(e.target.value)}
                          >
                            {shopifyStoreIntegrations.map((integ) => (
                              <option key={integ.id} value={integ.id}>
                                {integ.displayname || integ.name || `Store ${integ.id}`}
                              </option>
                            ))}
                          </select>
                        </label>
                        <p style={{ marginTop: 10 }}>
                          We&apos;ll fetch all variants with SKUs from the selected store and run them through the same preview shown above.
                        </p>
                      </>
                    ) : (
                      <p>{shopifyStoreError || 'No Shopify integrations were found for this client.'}</p>
                    )}
                    {shopifyStoreError && shopifyStoreIntegrations.length > 0 && (
                      <div className="error-text" style={{ marginTop: 10 }}>{shopifyStoreError}</div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn secondary"
                  onClick={() => setShopifyStoreModalOpen(false)}
                  disabled={shopifyStoreLoading}
                >
                  Cancel
                </button>
                <button
                  className={`btn primary ${(!shopifyStoreSelectedId || shopifyStoreLoading || shopifyStoreIntegrations.length === 0) ? 'disabled-grey' : ''}`}
                  onClick={handleShopifyStoreConfirm}
                  disabled={!shopifyStoreSelectedId || shopifyStoreLoading || shopifyStoreIntegrations.length === 0}
                >
                  Fetch Products
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Preview Modal */}
        {showImportPreview && (
          <div className="modal-backdrop items-modal-backdrop" onClick={() => setShowImportPreview(false)}>
            <div
              className="modal items-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="importPreviewTitle"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="importPreviewTitle">Items Import Preview</h3>
                <button
                  className="icon-btn"
                  onClick={() => {
                    cancelImportProgress();
                    setShowImportPreview(false);
                  }}
                  aria-label="Close preview"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Review the data below. Rows with <span className="preview-valid">✓</span> are valid and will be imported.
                  Rows with <span className="preview-error">!</span> have errors and must be corrected before importing.
                </p>
                {/* Summary counts (above the table) */}
                {previewData.length > 0 && (
                  <div className="preview-summary">
                    <strong>{validCount}</strong> valid, <strong>{invalidCount}</strong> invalid
                    {invalidCount > 0 && (
                      <div className="preview-warning">
                        Please correct all errors in your CSV and re-upload before importing.
                      </div>
                    )}
                  </div>
                )}
                {previewErrors.length > 0 && (
                  <div className="help-section">
                    <h4>Errors</h4>
                    <ul className="error-list">
                      {previewErrors.map((err, idx) => (
                        <li key={idx} className="error-item">{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="preview-table-wrapper" style={{ overflow: 'auto' }}>
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Action</th>
                        <th>Matched Item ID</th>
                        <th>Lookup SKU</th>
                        <th>Lookup Client ID</th>
                        <th>SKU</th>
                        <th>Description</th>
                        <th>Part #</th>
                        <th>UPC</th>
                        <th>HS Code</th>
                        <th>Client Id</th>
                        <th>Length</th>
                        <th>Width</th>
                        <th>Height</th>
                        <th>Weight</th>
                        <th>Volume</th>
                        <th>Unit Qty</th>
                        <th>Default Unit Value (USD)</th>
                        <th>Style</th>
                        <th>Color</th>
                        <th>Size</th>
                        <th>Default COO</th>
                        <th>Default Package Type</th>
                        <th>Default Weight UOM</th>
                        <th>Default Qty UOM</th>
                        <th>Manufacturer ID Code</th>
                        <th>Image</th>
                        <th>Own Shipping Container</th>
                        <th>Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, idx) => (
                        <tr key={idx} className={row.valid ? 'valid-row' : 'error-row'}>
                          <td>{row.valid ? <span className="preview-valid">✓</span> : <span className="preview-error">!</span>}</td>
                          <td>{row.valid ? (row.preview_action || 'Create') : ''}</td>
                          <td>{row.existing_itemid || ''}</td>
                          <td>{row.lookup_sku || ''}</td>
                          <td>{row.lookup_clientid || ''}</td>
                          <td>{row.sku}</td>
                          <td>{row.description}</td>
                          <td>{row.partnumber}</td>
                          <td>{row.upc}</td>
                          <td>{row.hscode || ''}</td>
                          <td>{row.clientid || ''}</td>
                          <td>{row.length || ''}</td>
                          <td>{row.width || ''}</td>
                          <td>{row.height || ''}</td>
                          <td>{row.weight || ''}</td>
                          <td>{row.volume || ''}</td>
                          <td>{row.unitqty || ''}</td>
                          <td>{row.default_unit_value != null ? formatUSD(row.default_unit_value) : ''}</td>
                          <td>{row.style || ''}</td>
                          <td>{row.color || ''}</td>
                          <td>{row.size || ''}</td>
                          <td>{row.default_country_of_origin || ''}</td>
                          <td>{row.default_package_type || ''}</td>
                          <td>{row.default_weight_uom || ''}</td>
                          <td>{row.default_qty_uom || ''}</td>
                          <td>{row.manufacturer_id_code || ''}</td>
                          <td style={{ maxWidth: 140, overflowWrap: 'anywhere' }}>
                            {renderPreviewImageCell(row.image1)}
                          </td>
                          <td>{row.own_shipping_container || ''}</td>
                          <td>
                            {(row.errors && row.errors.length > 0) ? (
                              <FaInfoCircle
                                className="preview-error"
                                title={row.errors.join('\n')}
                                style={{ cursor: 'help' }}
                              />
                            ) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn secondary"
                  onClick={() => {
                    cancelImportProgress();
                    setShowImportPreview(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className={`btn primary ${validCount === 0 || invalidCount > 0 || importLoading || progressOpen
                      ? 'disabled-grey'
                      : ''
                    }`}
                  onClick={handleConfirmImport}
                  disabled={
                    validCount === 0 || invalidCount > 0 || importLoading || progressOpen
                  }
                  title={
                    invalidCount > 0
                      ? 'Fix all errors in the CSV, then re-upload before importing.'
                      : (progressOpen ? 'Import already in progress' : '')
                  }
                >
                  {(importLoading || progressOpen) && <span className="spinner" />}
                  {importLoading || progressOpen
                    ? 'Importing…'
                    : `Import ${validCount} Valid Row${validCount === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Progress Modal (Preview or Import) */}
        {progressOpen && (
          <div className="modal-backdrop items-modal-backdrop" onClick={() => { /* prevent backdrop close while active */ }}>
            <div
              className="modal items-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="progressTitle"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="progressTitle">{inImport ? 'Importing Items' : 'Processing Items Preview'}</h3>
                <button className="icon-btn" onClick={inImport ? cancelImportProgress : cancelPreviewUpload} aria-label="Cancel">×</button>
              </div>
              <div className="modal-body">
                <div className="progress-wrap">
                  <div className="progress-label">{progressMsg || 'Working…'}</div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${Math.max(1, Math.min(progressPct, 100))}%` }} />
                  </div>
                  <div className="progress-percent">{Math.max(1, Math.min(progressPct, 100))}%</div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn secondary" onClick={inImport ? cancelImportProgress : cancelPreviewUpload}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirm Modal */}
        {showDeleteConfirm && (
          <div className="modal-backdrop" onClick={() => setShowDeleteConfirm(false)}>
            <div
              className="modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bulkDeleteTitle"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 520, width: '90vw', height: 'auto' }}
            >
              <div className="modal-header">
                <h3 id="bulkDeleteTitle">Delete Selected Items</h3>
                <button className="icon-btn" onClick={() => setShowDeleteConfirm(false)} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete <strong>{selectedIds.size}</strong> selected item{selectedIds.size === 1 ? '' : 's'}?
                  <br />
                  <span style={{ color: '#d64545' }}>This is a soft delete.</span>
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                  Cancel
                </button>
                <button className="btn danger" onClick={confermBulkDelete} disabled={deleting}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Label CSV Preview Modal */}
        {labelCsvModalOpen && (
          <div className="modal-backdrop" onClick={closeLabelCsvModal}>
            <div
              className="modal items-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="labelCsvTitle"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 720, width: '95vw' }}
            >
              <div className="modal-header">
                <h3 id="labelCsvTitle">Print Labels from CSV</h3>
                <button className="icon-btn" onClick={closeLabelCsvModal} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <div style={{ marginBottom: 12 }}>
                  <div><strong>File:</strong> {labelCsvFileName || 'n/a'}</div>
                  <div>
                    <strong>Ready:</strong> {labelCsvValidRows.length} item(s){' '}
                    {labelCsvTotalLabels > 0 && (
                      <span>({labelCsvTotalLabels} total label{labelCsvTotalLabels === 1 ? '' : 's'})</span>
                    )}
                  </div>
                  {labelCsvLoading && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="spinner" />
                      Processing CSV…
                    </div>
                  )}
                </div>
                {labelCsvErrors.length > 0 && (
                  <div style={{ background: '#3a1a1a', border: '1px solid #a44', padding: 10, borderRadius: 4, color: '#f5dada', marginBottom: 12 }}>
                    {labelCsvErrors.map((err, idx) => (
                      <div key={`${err}-${idx}`}>{err}</div>
                    ))}
                  </div>
                )}
                <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #2f2f2f', borderRadius: 4 }}>
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Row</th>
                        <th>Client ID</th>
                        <th>SKU</th>
                        <th style={{ width: 80 }}>Labels</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {labelCsvRows.length === 0 && !labelCsvLoading && (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', padding: 16 }}>No rows to display.</td>
                        </tr>
                      )}
                      {labelCsvRows.map((row, idx) => (
                        <tr key={`${row.rowNumber}-${idx}`}>
                          <td>{row.rowNumber}</td>
                          <td>{row.clientid || <em>n/a</em>}</td>
                          <td>{row.sku || <em>n/a</em>}</td>
                          <td>{row.qty || 0}</td>
                          <td style={{ color: row.status === 'error' ? '#ff8686' : (row.status === 'ok' ? '#6ad36a' : '#f3d28c') }}>
                            {row.status === 'ok' && 'Ready'}
                            {row.status === 'pending' && 'Pending'}
                            {row.status === 'error' && (row.message || 'Error')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={closeLabelCsvModal}>Cancel</button>
                <button
                  className="btn primary"
                  onClick={startLabelPrintFromCsv}
                  disabled={labelCsvValidRows.length === 0 || labelCsvLoading}
                >
                  {labelCsvValidRows.length ? `Use ${labelCsvTotalLabels || labelCsvValidRows.length} Label(s)` : 'Use Rows'}
                </button>
              </div>
            </div>
          </div>
        )}
        {labelCsvLoading && !labelCsvModalOpen && (
          <div className="modal-backdrop" onClick={(e) => e.stopPropagation()}>
            <div
              className="modal items-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="labelCsvLoadingTitle"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 400, width: '90vw', textAlign: 'center' }}
            >
              <div className="modal-header">
                <h3 id="labelCsvLoadingTitle">Importing CSV</h3>
              </div>
              <div className="modal-body">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <span className="spinner" />
                  <div>Importing CSV for printing labels…</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Print Label Modal (list view) */}
        {labelOpen && (
          <div className="modal-backdrop items-modal-backdrop" onClick={() => setLabelOpen(false)}>
            <div className="modal items-label-modal" onClick={e => e.stopPropagation()} aria-modal="true" role="dialog" aria-labelledby="labelTitle">
              <div className="modal-header">
                <h3 id="labelTitle">Print Item Label</h3>
                <button className="icon-button" onClick={() => setLabelOpen(false)} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {labelTargetEntries.length > 1 ? (
                    <div>
                      <strong>{labelTargetEntries.length}</strong> item(s) selected •{' '}
                      <strong>{labelTargetTotal}</strong> total label{labelTargetTotal === 1 ? '' : 's'}.
                    </div>
                  ) : labelTargetEntries.length === 1 ? (
                    <>
                      <div><strong>SKU:</strong> {labelTargetEntries[0].sku || labelItem?.sku}</div>
                      <div><strong>Description:</strong> {labelTargetEntries[0].description || labelItem?.description}</div>
                      {labelTargetTotal > 1 && (
                        <div><strong>Copies:</strong> {labelTargetTotal}</div>
                      )}
                    </>
                  ) : (
                    <>
                      <div><strong>SKU:</strong> {labelItem?.sku}</div>
                      <div><strong>Description:</strong> {labelItem?.description}</div>
                    </>
                  )}
                  <fieldset style={{ border:'none', padding:0, margin:0 }}>
                    <legend>Paper Type</legend>
                    <label style={{ display:'block', marginBottom:6 }}><input type="radio" name="paper" value="4x6" checked={labelPaper==='4x6'} onChange={()=>setLabelPaper('4x6')} /> 4x6 Label</label>
                    <label style={{ display:'block', marginBottom:6 }}><input type="radio" name="paper" value="4x2" checked={labelPaper==='4x2'} onChange={()=>setLabelPaper('4x2')} /> 4x2 Label</label>
                    <label style={{ display:'block' }}><input type="radio" name="paper" value="ol125wx" checked={labelPaper==='ol125wx'} onChange={()=>setLabelPaper('ol125wx')} /> 8.5 x 11 (OL125WX)</label>
                  </fieldset>
                </div>
              </div>
              <div className="modal-footer" style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
                <button className="btn" onClick={() => setLabelOpen(false)}>Cancel</button>
                <button className="btn primary" onClick={printLabel}><FaBarcode style={{ marginRight:6 }} /> Print</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
    <div className="items-full-wrapper">
      {leftPane}
      {/* Editor Modal */}
      {editorVisible && (
        <div className="modal-backdrop" onClick={() => { if (creating) setSelected(null); setEditing(false); setCreating(false); }}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="itemEditorTitle"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 900, width: '90vw', maxHeight: '90vh' }}
          >
            <div className="modal-header">
              <h3 id="itemEditorTitle">{creating ? 'New Item' : `Edit Item: ${selected?.sku || ''}`}</h3>
              <button
                className="icon-btn"
                onClick={() => { if (creating) setSelected(null); setEditing(false); setCreating(false); }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {editorForm}
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                type="button"
                onClick={() => { if (creating) setSelected(null); setEditing(false); setCreating(false); }}
                style={{ marginRight: 10 }}
              >
                <FaTimes size={16} /> Close
              </button>
              <button className="btn primary" onClick={saveItem} disabled={saving || !selected?.clientid}>
                <FaRegSave size={16} /> {saving ? 'Saving…' : 'Save Item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Print Label Modal (editor view) */}
    {labelOpen && (
      <div className="modal-backdrop items-modal-backdrop" onClick={() => setLabelOpen(false)}>
        <div className="modal items-label-modal" onClick={e => e.stopPropagation()} aria-modal="true" role="dialog" aria-labelledby="labelTitle">
          <div className="modal-header">
            <h3 id="labelTitle">Print Item Label</h3>
            <button className="icon-button" onClick={() => setLabelOpen(false)} aria-label="Close">×</button>
          </div>
          <div className="modal-body">
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {labelTargetEntries.length > 1 ? (
                <div>
                  <strong>{labelTargetEntries.length}</strong> item(s) selected •{' '}
                  <strong>{labelTargetTotal}</strong> total label{labelTargetTotal === 1 ? '' : 's'}.
                </div>
              ) : labelTargetEntries.length === 1 ? (
                <>
                  <div><strong>SKU:</strong> {labelTargetEntries[0].sku || labelItem?.sku}</div>
                  <div><strong>Description:</strong> {labelTargetEntries[0].description || labelItem?.description}</div>
                  {labelTargetTotal > 1 && (
                    <div><strong>Copies:</strong> {labelTargetTotal}</div>
                  )}
                </>
              ) : (
                <>
                  <div><strong>SKU:</strong> {labelItem?.sku}</div>
                  <div><strong>Description:</strong> {labelItem?.description}</div>
                </>
              )}
              <fieldset style={{ border:'none', padding:0, margin:0 }}>
                <legend>Paper Type</legend>
                <label style={{ display:'block', marginBottom:6 }}><input type="radio" name="paper" value="4x6" checked={labelPaper==='4x6'} onChange={()=>setLabelPaper('4x6')} /> 4x6 Label</label>
                <label style={{ display:'block', marginBottom:6 }}><input type="radio" name="paper" value="4x2" checked={labelPaper==='4x2'} onChange={()=>setLabelPaper('4x2')} /> 4x2 Label</label>
                <label style={{ display:'block' }}><input type="radio" name="paper" value="ol125wx" checked={labelPaper==='ol125wx'} onChange={()=>setLabelPaper('ol125wx')} /> 8.5 x 11 (OL125WX)</label>
              </fieldset>
            </div>
          </div>
          <div className="modal-footer" style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
            <button className="btn" onClick={() => setLabelOpen(false)}>Cancel</button>
            <button className="btn primary" onClick={printLabel}><FaBarcode style={{ marginRight:6 }} /> Print</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
